import {
  CoverLimit,
  Coverage,
  CoverageItems,
  InfoModal,
  Logo,
  ProductInfo,
  customBreakpoints,
  transformCoverage,
  transformExtraCoverage,
  variants
} from "./chunk-V5XP6XCT.js";
import {
  apiRequest,
  useMutationStatus
} from "./chunk-6YYILA2X.js";
import {
  AIRBRAKE_PROJECT_ID,
  AIRBRAKE_PROJECT_KEY,
  ANNUAL,
  MONTHLY,
  YEARLY,
  camelToTitleCase,
  creditRatingAgencyDescriptions,
  paymentToggleAtom,
  qcpLayoutToggleAtom,
  quoteComparisonPageAtom,
  sliderStateAtom,
  useExperiment,
  useLocale,
  useTokens,
  useTokensValues
} from "./chunk-KA7SO5SJ.js";

// src/components/AgentActions/AgentActions.tsx
import { arrowDownToBracket } from "@simplybusiness/icons";
import {
  Icon,
  Link,
  Button
} from "@simplybusiness/mobius";
import classNames from "classnames/dedupe";
import { forwardRef } from "react";

// src/components/AgentActions/handleDownload.ts
var handleDownload = (downloadQuoteUrl) => {
  fetch(downloadQuoteUrl).then((response) => {
    if (!response.ok) {
      throw new Error("Error fetching the file");
    }
    return response.blob();
  }).then((blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "downloaded-file.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }).catch((error) => {
    console.error(error);
  });
};

// src/components/AgentActions/AgentActions.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var AgentActions = forwardRef(
  (props, ref) => {
    const {
      elementType: Element = "div",
      newQuoteUrl,
      downloadQuoteUrl,
      componentName,
      ...otherProps
    } = props;
    const classes = classNames(
      "mobius",
      "mobius-agent-actions",
      otherProps.className
    );
    const { variant } = otherProps;
    return /* @__PURE__ */ jsxs(
      Element,
      {
        ref,
        ...otherProps,
        ...componentName ? { "data-component": componentName } : {},
        ...variant ? { "data-variant": variant } : {},
        className: classes,
        children: [
          /* @__PURE__ */ jsx(Link, { href: newQuoteUrl, className: "mobius-agent-actions__link", children: "Start New Quote" }),
          /* @__PURE__ */ jsxs(
            Button,
            {
              size: "sm",
              className: "mobius-agent-actions__button",
              onClick: () => handleDownload(downloadQuoteUrl),
              children: [
                /* @__PURE__ */ jsx(Icon, { icon: arrowDownToBracket, title: "Download icon" }),
                "Download Quote Proposal"
              ]
            }
          )
        ]
      }
    );
  }
);
AgentActions.displayName = "AgentActions";

// src/components/AgentInformation/AgentInformation.tsx
import {
  Text
} from "@simplybusiness/mobius";
import classNames2 from "classnames/dedupe";
import { forwardRef as forwardRef2 } from "react";
import { jsx as jsx2, jsxs as jsxs2 } from "react/jsx-runtime";
var AgentInformation = forwardRef2(
  (props, ref) => {
    const {
      elementType: Element = "div",
      componentName,
      ...otherProps
    } = props;
    const classes = classNames2(
      "mobius",
      "mobius-agent-information",
      otherProps.className
    );
    const { variant } = otherProps;
    return /* @__PURE__ */ jsxs2(
      Element,
      {
        ref,
        ...otherProps,
        ...componentName ? { "data-component": componentName } : {},
        ...variant ? { "data-variant": variant } : {},
        className: classes,
        children: [
          /* @__PURE__ */ jsx2(Text, { children: "You should receive an email with this information in about 5 minutes. It includes a link to return to the quote." }),
          /* @__PURE__ */ jsx2(Text, { children: /* @__PURE__ */ jsx2("strong", { children: "Please note that this email will not go to your client" }) }),
          /* @__PURE__ */ jsx2(Text, { children: /* @__PURE__ */ jsx2("strong", { children: "If you share the quote email with your client and they would like to purchase, you will need to complete the binding process on behalf of your client by clicking the \u201CSelect\u201C button above and entering the client\u2018s credit card information." }) })
        ]
      }
    );
  }
);
AgentInformation.displayName = "AgentInformation";

// src/hooks/useQuoteComparisonPageState/useQuoteComparisonPageState.tsx
import { useAtom } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
var useSetQcpState = (components) => {
  useHydrateAtoms([[quoteComparisonPageAtom, components]]);
};
var useQuoteComparisonPageState = () => useAtom(quoteComparisonPageAtom);

// src/components/CoverTogglesContainer/CoverToggle/CoverToggle.tsx
import { Checkbox, Option, Select } from "@simplybusiness/mobius";
import classNames3 from "classnames";
import { useEffect, useState } from "react";

// src/components/CoverTogglesContainer/CoverToggle/getCoverToggleValue.ts
var getCoverToggleValue = ({
  inputType,
  checked,
  value,
  toggleOnValue = "",
  toggleOffValue = ""
}) => {
  if (inputType === "checkbox") {
    return checked ? toggleOnValue : toggleOffValue;
  }
  return value;
};

// src/components/CoverTogglesContainer/CoverToggle/CoverToggle.tsx
import { jsx as jsx3 } from "react/jsx-runtime";
var CoverToggle = (props) => {
  const {
    fieldName,
    questionText,
    value,
    options,
    toggleOnValue,
    toggleOffValue,
    selected,
    onChange,
    isLoading,
    isError
  } = props;
  const [selectValue, setSelectValue] = useState(value);
  const [checkedValue, setCheckedValue] = useState(selected ?? false);
  const isSelect = Array.isArray(options) && options.length > 0;
  const classes = classNames3("mobius-cover-toggle", {
    "--is-select": isSelect,
    "--is-checkbox": !isSelect
  });
  useEffect(() => {
    if (isError) {
      setSelectValue(value);
      setCheckedValue(selected ?? false);
    }
  }, [isError, selectValue, setCheckedValue, value, selected]);
  const handleChange = (e) => {
    const isCheckbox = e.target.type === "checkbox";
    const toValue = getCoverToggleValue({
      inputType: e.target.type,
      checked: isCheckbox ? e.target.checked : void 0,
      value: e.target.value,
      toggleOnValue,
      toggleOffValue
    });
    setSelectValue(toValue);
    if (!isLoading && onChange) {
      onChange({
        label: questionText,
        fromValue: value,
        name: fieldName,
        toValue
      });
    }
  };
  if (isSelect) {
    return /* @__PURE__ */ jsx3(
      Select,
      {
        name: fieldName,
        label: questionText,
        value: selectValue,
        className: classes,
        onChange: handleChange,
        isDisabled: isLoading,
        children: (options || []).map((option) => /* @__PURE__ */ jsx3(Option, { value: option.value, children: option.label }, option.value))
      }
    );
  }
  return /* @__PURE__ */ jsx3(
    Checkbox,
    {
      name: fieldName,
      label: questionText,
      value,
      className: classes,
      onChange: handleChange,
      defaultSelected: checkedValue,
      isDisabled: isLoading
    }
  );
};

// src/components/CoverTogglesContainer/CoverTogglesContainer.tsx
import { Accordion, Button as Button2, Icon as Icon2, useBreakpoint } from "@simplybusiness/mobius";
import { rightArrow } from "@simplybusiness/icons";
import classNames5 from "classnames";

// src/hooks/useToggleCovers/useToggleCovers.ts
import { getAirbrake } from "@simplybusiness/services";
import { useMutation } from "@tanstack/react-query";
var airbrake = getAirbrake({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY
});
var useToggleCovers = () => {
  const [tokens, setTokens] = useTokens();
  const [, setComponents] = useQuoteComparisonPageState();
  const mutation = useMutation({
    mutationFn: (cover) => apiRequest("?step_action=toggle_qcp_component_cover", {
      ...tokens,
      coverToggleData: [
        {
          coverName: cover.name,
          coverValue: cover.value
        }
      ]
    }),
    mutationKey: ["toggleCovers"]
  });
  const { isPending, isError, error, isSuccess, mutate } = mutation;
  const toggleCovers = (cover) => {
    mutate(cover, {
      onSuccess: ({ data }) => {
        setComponents(data.layout.components);
        setTokens(data.configuration);
      },
      onError: (error2) => {
        console.error(`Error toggling covers: ${error2.message}`);
        void airbrake.notify(error2);
      }
    });
  };
  return {
    toggleCovers,
    isSuccess,
    isPending,
    isError,
    error
  };
};

// src/components/shared/PaymentToggle/PaymentToggle.tsx
import { useSnowplowContext } from "@simplybusiness/services";
import classNames4 from "classnames";
import { useAtom as useAtom3 } from "jotai";

// src/hooks/usePaymentToggleState/usePaymentToggleState.tsx
import { useAtom as useAtom2 } from "jotai";
import { useHydrateAtoms as useHydrateAtoms2 } from "jotai/utils";
var useSetPaymentToggleAtom = (paymentPeriod = ANNUAL) => {
  useHydrateAtoms2([[paymentToggleAtom, paymentPeriod]]);
};
var usePaymentToggleState = () => useAtom2(paymentToggleAtom);

// src/hooks/useTogglePaymentPreference/useTogglePaymentPreference.ts
import { getAirbrake as getAirbrake2 } from "@simplybusiness/services";
import { useMutation as useMutation2 } from "@tanstack/react-query";
var airbrake2 = getAirbrake2({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY
});
var useTogglePaymentPreference = () => {
  const tokens = useTokensValues();
  const mutation = useMutation2({
    mutationFn: (paymentPreference) => apiRequest("?step_action=toggle_payment_preference", {
      ...tokens,
      paymentPreference
    }),
    mutationKey: ["togglePaymentPreference"]
  });
  const { isPending, isError, error, isSuccess, mutate } = mutation;
  const togglePaymentPreference = (paymentPreference) => {
    mutate(paymentPreference, {
      onSuccess: () => {
      },
      // No response to handle
      onError: (error2) => {
        console.error(`Error toggling payment preference: ${error2.message}`);
        void airbrake2.notify(error2);
      }
    });
  };
  return {
    togglePaymentPreference,
    isSuccess,
    isPending,
    isError,
    error
  };
};

// src/components/shared/PaymentToggle/PaymentToggle.tsx
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var PaymentToggle = ({
  paymentTogglePeriod,
  shouldRecordPreference
}) => {
  const { snowplow } = useSnowplowContext();
  useSetPaymentToggleAtom(paymentTogglePeriod);
  const [paymentPeriod, setPaymentPeriod] = usePaymentToggleState();
  const [sliderIsOpen] = useAtom3(sliderStateAtom);
  const { togglePaymentPreference } = useTogglePaymentPreference();
  const { isUK } = useLocale();
  const isAnnual = paymentPeriod === ANNUAL;
  const handleClick = () => {
    const selectedPeriod = paymentPeriod === MONTHLY ? ANNUAL : MONTHLY;
    setPaymentPeriod(selectedPeriod);
    const category = `${sliderIsOpen ? "quote_details_slider" : "qcp"}_payment_toggle`;
    snowplow?.trigger("paymentToggleClicked", {
      category,
      label: isAnnual ? "monthly payments" : "one-time payment"
    });
    if (shouldRecordPreference) {
      togglePaymentPreference(selectedPeriod);
    }
  };
  const buttonClasses = classNames4("mobius-payment-toggle", {
    "--is-toggled": isAnnual
  });
  return /* @__PURE__ */ jsxs3(
    "button",
    {
      type: "button",
      className: buttonClasses,
      onClick: handleClick,
      role: "switch",
      "aria-checked": isAnnual,
      "aria-label": `Switch to ${isAnnual ? MONTHLY : YEARLY} payment`,
      "data-testid": "payment-toggle",
      children: [
        /* @__PURE__ */ jsx4(
          "div",
          {
            className: "mobius-payment-toggle__slider",
            role: "presentation",
            "aria-hidden": "true"
          }
        ),
        /* @__PURE__ */ jsx4(
          "span",
          {
            className: "mobius-payment-toggle__text --monthly",
            "aria-hidden": isAnnual,
            children: "Monthly"
          }
        ),
        /* @__PURE__ */ jsx4(
          "span",
          {
            className: "mobius-payment-toggle__text --yearly",
            "aria-hidden": !isAnnual,
            children: isUK ? "Annual" : "Yearly"
          }
        )
      ]
    }
  );
};

// src/components/CoverTogglesContainer/CoverTogglesContainer.tsx
import { useSnowplowContext as useSnowplowContext2 } from "@simplybusiness/services";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
var CoverTogglesContainer = (props) => {
  const {
    coverOptionsUrl,
    coverTogglesData,
    componentName,
    variant,
    paymentPeriod,
    showToggle = false,
    shouldRecordPreference = false
  } = props;
  const { up } = useBreakpoint();
  const isDesktop = up("lg");
  const isPaymentAside = variant === "payment-aside";
  const outerClasses = classNames5("mobius-cover-toggles-container__outer", {
    "--is-payment-aside": isPaymentAside
  });
  const classes = classNames5("mobius-cover-toggles-container", {
    "--is-collapsible": isPaymentAside,
    "--is-desktop": isDesktop
  });
  const { snowplow } = useSnowplowContext2();
  const { toggleCovers, isPending, isError } = useToggleCovers();
  const showCoverToggles = coverTogglesData?.length > 0;
  const CoverToggles = (coverTogglesData || []).map((toggle) => /* @__PURE__ */ jsx5(
    CoverToggle,
    {
      ...toggle,
      onChange: (cover) => {
        toggleCovers({
          name: toggle.fieldName,
          value: cover.toValue
        });
      },
      isLoading: isPending,
      isError
    },
    toggle.fieldName
  ));
  const CoverTogglesContainerContent = () => /* @__PURE__ */ jsx5("div", { className: classes, "data-testid": "cover-toggles-container", children: CoverToggles });
  const handleSelectCoverOptionsClick = () => {
    if (coverOptionsUrl) {
      snowplow?.trigger("selectCoverOptionsClicked", {});
      window.location.assign(coverOptionsUrl);
    }
  };
  return /* @__PURE__ */ jsxs4(
    "div",
    {
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: outerClasses,
      "data-testid": "cover-toggles-outer-container",
      children: [
        showCoverToggles && (isPaymentAside ? /* @__PURE__ */ jsx5(
          Accordion,
          {
            "data-testid": "cover-toggles-accordion",
            startOpen: isDesktop,
            showText: "Change limits",
            hideText: "Change limits",
            children: /* @__PURE__ */ jsx5(CoverTogglesContainerContent, {})
          }
        ) : /* @__PURE__ */ jsx5(CoverTogglesContainerContent, {})),
        coverOptionsUrl && /* @__PURE__ */ jsxs4(Button2, { onClick: handleSelectCoverOptionsClick, size: "sm", children: [
          "Edit your covers",
          " ",
          /* @__PURE__ */ jsx5(Icon2, { icon: rightArrow, size: "xs", color: "var(--color-neutral-100)" })
        ] }),
        showToggle && /* @__PURE__ */ jsx5(
          PaymentToggle,
          {
            paymentTogglePeriod: paymentPeriod,
            shouldRecordPreference
          }
        )
      ]
    }
  );
};

// src/components/CoverTogglesContainer/ExperimentGridView/CoverTogglesContainer.tsx
import { Accordion as Accordion2, Button as Button4, Icon as Icon4, useBreakpoint as useBreakpoint2 } from "@simplybusiness/mobius";
import { rightArrow as rightArrow2 } from "@simplybusiness/icons";
import classNames7 from "classnames";

// src/components/CoverTogglesContainer/ExperimentGridView/LayoutToggle/LayoutToggle.tsx
import { Button as Button3, Icon as Icon3, Text as Text2 } from "@simplybusiness/mobius";
import { list, grid2 } from "@simplybusiness/icons";
import classNames6 from "classnames/dedupe";

// src/hooks/useSetLayoutToggleState/useSetLayoutToggleState.tsx
import { useHydrateAtoms as useHydrateAtoms3 } from "jotai/utils";
var useSetLayoutToggleAtom = (defaultLayout) => {
  const variant = useExperiment("uk2505Gridvslistview") || "";
  const isTreatment = ["treatment_grid", "treatment_list"].includes(variant);
  const layout = isTreatment ? defaultLayout || (variant === "treatment_grid" ? "grid" : "list") : "list";
  useHydrateAtoms3([[qcpLayoutToggleAtom, layout]]);
};

// src/hooks/useToggleLayoutPreference/useToggleLayoutPreference.ts
import { getAirbrake as getAirbrake3 } from "@simplybusiness/services";
import { useMutation as useMutation3 } from "@tanstack/react-query";
var airbrake3 = getAirbrake3({
  projectId: AIRBRAKE_PROJECT_ID,
  projectKey: AIRBRAKE_PROJECT_KEY
});
var useToggleLayoutPreference = () => {
  const tokens = useTokensValues();
  const mutation = useMutation3({
    mutationFn: (quotesLayoutPreference) => apiRequest("?step_action=toggle_layout_preference", {
      ...tokens,
      quotesLayoutPreference
    }),
    mutationKey: ["toggleLayoutPreference"]
  });
  const { isPending, isError, error, isSuccess, mutate } = mutation;
  const toggleLayoutPreference = (quotesLayoutPreference) => {
    mutate(quotesLayoutPreference, {
      onSuccess: () => {
      },
      // No response to handle
      onError: (error2) => {
        console.error(`Error toggling layout preference: ${error2.message}`);
        void airbrake3.notify(error2);
      }
    });
  };
  return {
    toggleLayoutPreference,
    isSuccess,
    isPending,
    isError,
    error
  };
};

// src/components/CoverTogglesContainer/ExperimentGridView/LayoutToggle/LayoutToggle.tsx
import { useAtom as useAtom4 } from "jotai";
import { useSnowplowContext as useSnowplowContext3 } from "@simplybusiness/services";
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var LayoutToggle = ({ quotesLayoutPreference }) => {
  useSetLayoutToggleAtom(quotesLayoutPreference);
  const { toggleLayoutPreference } = useToggleLayoutPreference();
  const [layout, setLayout] = useAtom4(qcpLayoutToggleAtom);
  const { snowplow } = useSnowplowContext3();
  const handleClick = (newLayout) => {
    setLayout(newLayout);
    toggleLayoutPreference(newLayout);
    snowplow?.trigger("layoutToggleClickedExperiment", {
      layout: newLayout
    });
  };
  return /* @__PURE__ */ jsxs5("div", { className: "mobius-layout-toggle", children: [
    /* @__PURE__ */ jsxs5(
      Button3,
      {
        variant: "link",
        className: classNames6("mobius-layout-toggle__item", {
          "mobius-layout-toggle__item--active": layout === "grid"
        }),
        onClick: () => handleClick("grid"),
        children: [
          /* @__PURE__ */ jsx6(Text2, { variant: "small", children: "Grid view" }),
          /* @__PURE__ */ jsx6(Icon3, { icon: grid2, title: "Switch to grid layout" })
        ]
      }
    ),
    /* @__PURE__ */ jsx6("div", { className: "mobius-layout-toggle__divider" }),
    /* @__PURE__ */ jsxs5(
      Button3,
      {
        variant: "link",
        className: classNames6("mobius-layout-toggle__item", {
          "mobius-layout-toggle__item--active": layout === "list"
        }),
        onClick: () => handleClick("list"),
        children: [
          /* @__PURE__ */ jsx6(Icon3, { icon: list, title: "Switch to list layout" }),
          /* @__PURE__ */ jsx6(Text2, { variant: "small", children: "List view" })
        ]
      }
    )
  ] });
};

// src/components/CoverTogglesContainer/ExperimentGridView/CoverTogglesContainer.tsx
import { useSnowplowContext as useSnowplowContext4 } from "@simplybusiness/services";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var CoverTogglesContainer2 = (props) => {
  const {
    coverOptionsUrl,
    coverTogglesData,
    componentName,
    variant,
    paymentPeriod,
    showToggle = false,
    shouldRecordPreference = false,
    quotesLayoutPreference
  } = props;
  const { up } = useBreakpoint2();
  const isDesktop = up("lg");
  const isPaymentAside = variant === "payment-aside";
  const outerClasses = classNames7("mobius-cover-toggles-container__outer", {
    "--is-payment-aside": isPaymentAside
  });
  const classes = classNames7("mobius-cover-toggles-container", {
    "--is-collapsible": isPaymentAside,
    "--is-desktop": isDesktop
  });
  const { snowplow } = useSnowplowContext4();
  const { toggleCovers, isPending, isError } = useToggleCovers();
  const showCoverToggles = coverTogglesData?.length > 0;
  const CoverToggles = (coverTogglesData || []).map((toggle) => /* @__PURE__ */ jsx7(
    CoverToggle,
    {
      ...toggle,
      onChange: (cover) => {
        toggleCovers({
          name: toggle.fieldName,
          value: cover.toValue
        });
      },
      isLoading: isPending,
      isError
    },
    toggle.fieldName
  ));
  const CoverTogglesContainerContent = () => /* @__PURE__ */ jsx7("div", { className: classes, "data-testid": "cover-toggles-container", children: CoverToggles });
  const handleSelectCoverOptionsClick = () => {
    if (coverOptionsUrl) {
      snowplow?.trigger("selectCoverOptionsClicked", {});
      window.location.assign(coverOptionsUrl);
    }
  };
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: outerClasses,
      "data-testid": "cover-toggles-outer-container",
      children: [
        showCoverToggles && (isPaymentAside ? /* @__PURE__ */ jsx7(
          Accordion2,
          {
            "data-testid": "cover-toggles-accordion",
            startOpen: isDesktop,
            showText: "Change limits",
            hideText: "Change limits",
            children: /* @__PURE__ */ jsx7(CoverTogglesContainerContent, {})
          }
        ) : /* @__PURE__ */ jsx7(CoverTogglesContainerContent, {})),
        /* @__PURE__ */ jsxs6("div", { className: "mobius-cover-toggles-container__extra-toggles", children: [
          /* @__PURE__ */ jsx7(LayoutToggle, { quotesLayoutPreference }),
          coverOptionsUrl && /* @__PURE__ */ jsxs6(Button4, { onClick: handleSelectCoverOptionsClick, size: "sm", children: [
            "Select other covers",
            " ",
            /* @__PURE__ */ jsx7(
              Icon4,
              {
                icon: rightArrow2,
                size: "xs",
                color: "var(--color-neutral-100)"
              }
            )
          ] }),
          showToggle && /* @__PURE__ */ jsx7(
            PaymentToggle,
            {
              paymentTogglePeriod: paymentPeriod,
              shouldRecordPreference
            }
          )
        ] })
      ]
    }
  );
};

// src/components/CoverTogglesContainer/index.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var CoverTogglesContainer3 = (props) => {
  const isTreatment = ["treatment_grid", "treatment_list"].includes(
    useExperiment("uk2505Gridvslistview") || ""
  );
  if (isTreatment) {
    return /* @__PURE__ */ jsx8(CoverTogglesContainer2, { ...props });
  }
  return /* @__PURE__ */ jsx8(CoverTogglesContainer, { ...props });
};

// src/components/Headline/Headline.tsx
import classNames8 from "classnames/dedupe";
import { forwardRef as forwardRef3 } from "react";

// src/components/Headline/ExtraUKText.tsx
import { Container, Link as Link2, Text as Text3 } from "@simplybusiness/mobius";
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
function ExtraUKText({ phoneNumber }) {
  return /* @__PURE__ */ jsx9(Container, { children: /* @__PURE__ */ jsxs7(
    Text3,
    {
      className: "mobius-quote-headline__text mobius-quote-headline__text--variant-without-qdp-drawer",
      style: { color: "var(--color-text-lead)" },
      children: [
        "You can make changes to the covers you selected here: Can't find the cover you're looking for?",
        /* @__PURE__ */ jsx9("br", {}),
        "Give us a call on",
        " ",
        /* @__PURE__ */ jsx9(
          Link2,
          {
            "aria-label": "Contact telephone number",
            "data-testid": "phone-number-link",
            href: `tel:${phoneNumber}`,
            style: { color: "var(--color-text-lead)" },
            children: phoneNumber
          }
        ),
        " ",
        "and one of our team could help."
      ]
    }
  ) });
}

// src/components/Headline/QuoteHeadline.tsx
import { Container as Container2, Text as Text4 } from "@simplybusiness/mobius";

// src/utils/numberToWord.tsx
function numberToWord(number) {
  if (number < 0) return "zero";
  const words = [
    "zero",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
    "twenty"
  ];
  return words[number] || "More than twenty";
}

// src/components/Headline/QuoteHeadline.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var QuoteHeadline = ({
  quoteCount,
  customerName,
  trade,
  verticalHeadlineWording
}) => {
  if (quoteCount === 0) {
    return /* @__PURE__ */ jsx10("div", { className: "mobius-quote-headline__no-quotes-title", children: /* @__PURE__ */ jsx10("strong", { children: "Edit your limits and covers" }) });
  }
  const highlightClasses = "mobius-quote-headline__highlight";
  return /* @__PURE__ */ jsx10(Container2, { size: "sm", children: /* @__PURE__ */ jsxs8(
    Text4,
    {
      className: "mobius-quote-headline__text",
      style: { color: "var(--color-text-lead)" },
      children: [
        "Thanks",
        " ",
        /* @__PURE__ */ jsx10("span", { className: highlightClasses, children: /* @__PURE__ */ jsx10("strong", { className: "has-pii", children: customerName }) }),
        ", we've searched through our trusted panel of insurers and found",
        " ",
        /* @__PURE__ */ jsx10("span", { className: highlightClasses, children: /* @__PURE__ */ jsx10("strong", { children: numberToWord(quoteCount) }) }),
        " ",
        "provider",
        quoteCount > 1 ? "s" : "",
        " who can give you a quote for your",
        " ",
        /* @__PURE__ */ jsx10("span", { className: highlightClasses, children: /* @__PURE__ */ jsx10("strong", { children: trade }) }),
        verticalHeadlineWording ? " " + verticalHeadlineWording + "." : "."
      ]
    }
  ) });
};

// src/components/Headline/QuoteHeadlineWithBacklink.tsx
import { Text as Text5, Container as Container3, Link as Link3, Icon as Icon5 } from "@simplybusiness/mobius";
import { leftArrow } from "@simplybusiness/icons";
import { useSnowplowContext as useSnowplowContext5 } from "@simplybusiness/services";
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var QuoteHeadlineWithBacklink = ({
  quoteCount,
  product,
  backLinkUrl,
  journeyId,
  vertical
}) => {
  const { snowplow } = useSnowplowContext5();
  if (quoteCount === 0) {
    return null;
  }
  const handleBackLinkClick = () => {
    if (journeyId && vertical) {
      snowplow?.trigger("backToApplication", { journeyId, vertical });
    }
  };
  return /* @__PURE__ */ jsxs9(Container3, { size: "sm", children: [
    backLinkUrl && /* @__PURE__ */ jsxs9(
      Link3,
      {
        href: backLinkUrl,
        className: "mobius-quote-headline__link",
        onClick: handleBackLinkClick,
        children: [
          /* @__PURE__ */ jsx11(
            Icon5,
            {
              icon: leftArrow,
              size: "xs",
              className: "mobius-quote-headline__back-icon",
              title: "Return to previous step"
            }
          ),
          /* @__PURE__ */ jsx11("span", { children: "Back" })
        ]
      }
    ),
    /* @__PURE__ */ jsxs9(Text5, { className: "mobius-quote-headline__title", children: [
      "Your ",
      /* @__PURE__ */ jsx11("strong", { children: product }),
      " quotes"
    ] }),
    /* @__PURE__ */ jsxs9(Text5, { className: "mobius-quote-headline__description", children: [
      "We searched through multiple carriers and found",
      " ",
      /* @__PURE__ */ jsxs9("strong", { children: [
        quoteCount,
        " quote",
        quoteCount > 1 ? "s" : "",
        " "
      ] }),
      "for your business"
    ] })
  ] });
};

// src/components/Headline/QuoteHeadlineWithEditlink.tsx
import { Container as Container4, Link as Link4, Text as Text6 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext6 } from "@simplybusiness/services";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
var QuoteHeadlineWithEditlink = ({
  quoteCount,
  product,
  backLinkUrl,
  journeyId,
  vertical
}) => {
  const { snowplow } = useSnowplowContext6();
  if (quoteCount === 0) {
    return null;
  }
  const handleBackLinkClick = () => {
    if (journeyId && vertical) {
      snowplow?.trigger("backToApplication", { journeyId, vertical });
    }
  };
  return /* @__PURE__ */ jsxs10(Container4, { size: "sm", children: [
    backLinkUrl && /* @__PURE__ */ jsx12(
      Link4,
      {
        href: backLinkUrl,
        className: "mobius-quote-headline__link",
        onClick: handleBackLinkClick,
        children: /* @__PURE__ */ jsx12("span", { children: "Edit application" })
      }
    ),
    /* @__PURE__ */ jsxs10(Text6, { className: "mobius-quote-headline__title", children: [
      "Your ",
      /* @__PURE__ */ jsx12("strong", { children: product }),
      " quotes"
    ] }),
    /* @__PURE__ */ jsxs10(Text6, { className: "mobius-quote-headline__description", children: [
      "We searched through multiple carriers and found",
      " ",
      /* @__PURE__ */ jsxs10("strong", { children: [
        quoteCount,
        " quote",
        quoteCount > 1 ? "s" : "",
        " "
      ] }),
      "for your business"
    ] })
  ] });
};

// src/components/Headline/Headline.tsx
import { Fragment, jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var Headline = forwardRef3((props, ref) => {
  const {
    className,
    componentName,
    quoteCount,
    variant = "control",
    phoneNumber,
    ...otherProps
  } = props;
  const classes = classNames8("mobius", "mobius-quote-headline", className);
  const { isUK } = useLocale();
  const showExtraText = quoteCount > 0;
  const passProps = { ...otherProps, quoteCount };
  return /* @__PURE__ */ jsxs11(
    "div",
    {
      ref,
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      className: classes,
      children: [
        variant === "backlink" && /* @__PURE__ */ jsx13(
          QuoteHeadlineWithBacklink,
          {
            ...passProps
          }
        ),
        variant === "editlink" && /* @__PURE__ */ jsx13(
          QuoteHeadlineWithEditlink,
          {
            ...passProps
          }
        ),
        isUK && /* @__PURE__ */ jsxs11(Fragment, { children: [
          /* @__PURE__ */ jsx13(
            QuoteHeadline,
            {
              ...passProps
            }
          ),
          showExtraText && /* @__PURE__ */ jsx13(ExtraUKText, { phoneNumber })
        ] })
      ]
    }
  );
});
Headline.displayName = "Headline";

// src/components/Headline/HeadlineSkeleton.tsx
import { Link as Link5, Text as Text7 } from "@simplybusiness/mobius";
import { SkeletonTheme as SkeletonTheme2 } from "react-loading-skeleton";

// src/components/QuoteCards/QuotesSkeleton/index.tsx
import classNames10 from "classnames/dedupe";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";

// src/components/QuoteCards/Section/Section.tsx
import { Flex } from "@simplybusiness/mobius";
import classNames9 from "classnames/dedupe";
import { jsx as jsx14 } from "react/jsx-runtime";
var Section = ({
  children,
  color,
  className,
  ...otherProps
}) => {
  const classes = classNames9("mobius-quote-card__section", className, {
    "--is-light": color === "light",
    "--is-dark": color === "dark"
  });
  return /* @__PURE__ */ jsx14("div", { ...otherProps, className: classes, children: /* @__PURE__ */ jsx14(
    Flex,
    {
      flexDirection: "column",
      className: "mobius-quote-card__section-container",
      children
    }
  ) });
};

// src/components/QuoteCards/QuotesSkeleton/QuotesSkeleton.tsx
import { Flex as Flex2 } from "@simplybusiness/mobius";
import { jsx as jsx15, jsxs as jsxs12 } from "react/jsx-runtime";
var CardSkeleton = () => /* @__PURE__ */ jsxs12("li", { className: "mobius-quote-card", children: [
  /* @__PURE__ */ jsxs12(Flex2, { className: "mobius-quote-card__header", flexDirection: "column", children: [
    /* @__PURE__ */ jsx15(
      StyledSkeleton,
      {
        className: "mobius-quote-card__header-logo",
        height: "40px"
      }
    ),
    /* @__PURE__ */ jsxs12(Flex2, { className: "mobius-quote-card__ratings --has-reinsurer-text", children: [
      /* @__PURE__ */ jsx15(StyledSkeleton, {}),
      /* @__PURE__ */ jsx15(StyledSkeleton, {})
    ] })
  ] }),
  /* @__PURE__ */ jsxs12("div", { className: "mobius-quote-card__pricing", children: [
    /* @__PURE__ */ jsx15(StyledSkeleton, { width: "70%", height: "30px" }),
    /* @__PURE__ */ jsx15(StyledSkeleton, { width: "40%" }),
    /* @__PURE__ */ jsx15(StyledSkeleton, { style: { marginBottom: "10px" } }),
    /* @__PURE__ */ jsx15("div", { className: "mobius-quote-card__badges", children: /* @__PURE__ */ jsx15("div", { style: { width: "100%" }, children: /* @__PURE__ */ jsx15(StyledSkeleton, { height: "34px" }) }) })
  ] }),
  /* @__PURE__ */ jsxs12(
    Flex2,
    {
      flexDirection: "column",
      gap: "sm",
      className: "mobius-quote-card__coverage",
      children: [
        /* @__PURE__ */ jsx15(StyledSkeleton, { width: "56%" }),
        /* @__PURE__ */ jsx15(StyledSkeleton, { width: "45%" }),
        /* @__PURE__ */ jsx15(StyledSkeleton, {}),
        /* @__PURE__ */ jsx15(StyledSkeleton, {}),
        /* @__PURE__ */ jsx15(StyledSkeleton, { width: "50%" })
      ]
    }
  ),
  /* @__PURE__ */ jsxs12(
    Flex2,
    {
      justifyContent: "space-between",
      gap: "sm",
      className: "mobius-quote-actions",
      children: [
        /* @__PURE__ */ jsx15("div", { className: "mobius-quote-actions__button qa-view-details-option", children: /* @__PURE__ */ jsx15(StyledSkeleton, { height: "40px" }) }),
        /* @__PURE__ */ jsx15("div", { className: "mobius-quote-actions__button qa-buy-quote-option", children: /* @__PURE__ */ jsx15(StyledSkeleton, { height: "40px" }) })
      ]
    }
  )
] });

// src/components/QuoteCards/QuotesSkeleton/QuotesSkeletonUK.tsx
import { Flex as Flex3, useBreakpoint as useBreakpoint3 } from "@simplybusiness/mobius";
import { jsx as jsx16, jsxs as jsxs13 } from "react/jsx-runtime";
var CardSkeletonUK = () => {
  const { down } = useBreakpoint3(customBreakpoints);
  const isMobileDevice = down("md");
  return /* @__PURE__ */ jsxs13("li", { className: "mobius-quote-card", children: [
    /* @__PURE__ */ jsx16(
      Flex3,
      {
        className: "mobius-quote-card__header --is-uk",
        flexDirection: "column",
        gap: "xxs",
        children: /* @__PURE__ */ jsx16("div", { className: "mobius-quote-card__header-logo --is-uk", children: /* @__PURE__ */ jsx16(StyledSkeleton, { height: "40px" }) })
      }
    ),
    /* @__PURE__ */ jsxs13("div", { className: "mobius-quote-card__pricing --is-uk", children: [
      /* @__PURE__ */ jsxs13(Flex3, { justifyContent: "space-between", gap: "md", children: [
        /* @__PURE__ */ jsx16("div", { style: { width: "50%", marginBottom: "6px" }, children: /* @__PURE__ */ jsx16(StyledSkeleton, { height: "34px" }) }),
        !isMobileDevice && /* @__PURE__ */ jsx16("div", { style: { width: "50%" }, children: /* @__PURE__ */ jsx16(
          StyledSkeleton,
          {
            height: "34px",
            className: "mobius-quote-card__badges --is-uk"
          }
        ) })
      ] }),
      /* @__PURE__ */ jsxs13(Flex3, { style: { marginBottom: "6px" }, flexDirection: "column", gap: "xs", children: [
        /* @__PURE__ */ jsx16(StyledSkeleton, { width: "100%" }),
        /* @__PURE__ */ jsx16(StyledSkeleton, { width: "100%" })
      ] }),
      isMobileDevice && /* @__PURE__ */ jsx16(
        StyledSkeleton,
        {
          className: "mobius-quote-card__badges --is-uk",
          height: "34px",
          width: "50%"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs13("div", { className: "mobius-quote-card__coverage", children: [
      /* @__PURE__ */ jsx16("p", { className: "mobius mobius-text --is-h3 mobius-quote-card__coverage-title", children: /* @__PURE__ */ jsx16(StyledSkeleton, {}) }),
      !isMobileDevice && /* @__PURE__ */ jsxs13(
        Flex3,
        {
          gap: "sm",
          className: "mobius-quote-card__coverage-items",
          flexDirection: "column",
          children: [
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {}),
            /* @__PURE__ */ jsx16(StyledSkeleton, {})
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsx16(Flex3, { className: "mobius-quote-actions-form", children: /* @__PURE__ */ jsxs13(
      Flex3,
      {
        gap: "sm",
        justifyContent: "space-between",
        className: "mobius-quote-actions",
        children: [
          /* @__PURE__ */ jsx16("div", { className: "mobius-quote-actions__button mobius-quote-actions__button--is-details", children: /* @__PURE__ */ jsx16(StyledSkeleton, { height: "40px" }) }),
          /* @__PURE__ */ jsx16("div", { className: "mobius-quote-actions__button mobius-quote-actions__button--is-select", children: /* @__PURE__ */ jsx16(StyledSkeleton, { height: "40px" }) })
        ]
      }
    ) })
  ] });
};

// src/components/QuoteCards/QuotesSkeleton/index.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
var StyledSkeleton = (props) => /* @__PURE__ */ jsx17(
  Skeleton,
  {
    borderRadius: "var(--radius-2)",
    ...props,
    containerClassName: "mobius-skeleton"
  }
);
var QuotesSkeleton = ({ count = 3, variant }) => {
  const isUKVariant = variants(variant);
  const classes = classNames10("mobius-quote-cards__grid", {
    "--is-horizontal": isUKVariant
  });
  return /* @__PURE__ */ jsx17(
    SkeletonTheme,
    {
      baseColor: "var(--color-background-light)",
      highlightColor: "var(--color-background)",
      children: /* @__PURE__ */ jsx17(Section, { "data-variant": variant, children: /* @__PURE__ */ jsx17("ul", { className: classes, children: Array(count).fill(void 0).map(
        (_, index) => isUKVariant ? /* @__PURE__ */ jsx17(CardSkeletonUK, {}, index) : /* @__PURE__ */ jsx17(CardSkeleton, {}, index)
      ) }) })
    }
  );
};

// src/components/Headline/HeadlineSkeleton.tsx
import { Fragment as Fragment2, jsx as jsx18, jsxs as jsxs14 } from "react/jsx-runtime";
var HeadlineSkeleton = ({ quoteCount }) => {
  const { isUK } = useLocale();
  return /* @__PURE__ */ jsx18("div", { className: "mobius-quote-headline", children: /* @__PURE__ */ jsx18(
    SkeletonTheme2,
    {
      baseColor: "var(--color-background-light)",
      highlightColor: "var(--color-background)",
      children: isUK ? quoteCount === 0 ? /* @__PURE__ */ jsx18(
        "div",
        {
          className: "mobius-quote-headline__no-quotes-title",
          style: { display: "block", textAlign: "center" },
          children: /* @__PURE__ */ jsx18(StyledSkeleton, { width: "30%" })
        }
      ) : /* @__PURE__ */ jsxs14(
        "div",
        {
          className: "mobius-quote-headline__text",
          style: {
            height: "122px"
            /* Fix div height to match Text */
          },
          children: [
            /* @__PURE__ */ jsx18(StyledSkeleton, {}),
            /* @__PURE__ */ jsx18(StyledSkeleton, { width: "50%" }),
            /* @__PURE__ */ jsxs14("div", { className: "mobius-quote-headline__text mobius-quote-headline__text--variant-without-qdp-drawer", children: [
              /* @__PURE__ */ jsx18(StyledSkeleton, { width: "65%" }),
              /* @__PURE__ */ jsx18(StyledSkeleton, { width: "40%" })
            ] })
          ]
        }
      ) : /* @__PURE__ */ jsxs14(Fragment2, { children: [
        /* @__PURE__ */ jsx18(
          Link5,
          {
            style: { display: "block" },
            className: "mobius-quote-headline__link",
            children: /* @__PURE__ */ jsx18(StyledSkeleton, { width: "8%" })
          }
        ),
        /* @__PURE__ */ jsx18(Text7, { className: "mobius-quote-headline__title", children: /* @__PURE__ */ jsx18(StyledSkeleton, { width: "42%" }) }),
        /* @__PURE__ */ jsx18(Text7, { className: "mobius-quote-headline__description", children: /* @__PURE__ */ jsx18(StyledSkeleton, { width: "55%" }) })
      ] })
    }
  ) });
};

// src/components/Headline/index.tsx
import { jsx as jsx19 } from "react/jsx-runtime";
var Headline2 = (props) => {
  const isToggling = useMutationStatus("toggleCovers");
  if (isToggling) {
    return /* @__PURE__ */ jsx19(HeadlineSkeleton, { ...props });
  }
  return /* @__PURE__ */ jsx19(Headline, { ...props });
};

// src/components/QuoteCards/QuoteCard/filterCovers.tsx
var filterCovers = (quote) => quote.vertical === "workers_compensation" ? quote.covers.filter(
  (cover) => cover.displayName !== "Employer's Liability" && cover.displayName !== "Other States Coverage"
) : quote.covers;

// src/components/QuoteCards/QuoteCard/Header/Header.tsx
import { Flex as Flex5 } from "@simplybusiness/mobius";

// src/components/QuoteCards/QuoteCard/Ratings/Ratings.tsx
import { Box, Flex as Flex4, Text as Text8 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext7 } from "@simplybusiness/services";
import classNames11 from "classnames";
import { useAtom as useAtom5 } from "jotai";
import { useCallback } from "react";
import { Fragment as Fragment3, jsx as jsx20, jsxs as jsxs15 } from "react/jsx-runtime";
var Ratings = ({ data }) => {
  const { snowplow } = useSnowplowContext7();
  const { reinsurerText, ratingBody, ratingType, rating } = data;
  const ratingAgency = creditRatingAgencyDescriptions[ratingType];
  const blankSpace = /* @__PURE__ */ jsx20(Fragment3, { children: "\xA0" });
  const containerClasses = classNames11("mobius-quote-card__ratings", {
    "--has-reinsurer-text": reinsurerText
  });
  const [sliderIsOpen] = useAtom5(sliderStateAtom);
  const handleOpen = useCallback(() => {
    const category = `${sliderIsOpen ? "quote_details_slider" : "qcp"}_insurer_rating_help`;
    if (ratingAgency) {
      snowplow?.trigger("ratingsModalOpened", {
        category,
        label: ratingAgency.label
      });
    }
  }, [ratingAgency, sliderIsOpen, snowplow]);
  return /* @__PURE__ */ jsxs15(
    Flex4,
    {
      flexDirection: "column",
      className: containerClasses,
      "data-testid": "ratings-control",
      children: [
        /* @__PURE__ */ jsx20(
          Text8,
          {
            className: "mobius-quote-card__ratings-text",
            "data-testid": "ratings-text",
            children: reinsurerText || blankSpace
          }
        ),
        (ratingBody || rating) && /* @__PURE__ */ jsxs15(Box, { className: "mobius-quote-card__ratings-body", children: [
          ratingAgency && /* @__PURE__ */ jsx20(
            InfoModal,
            {
              toggleText: ratingBody || "",
              title: ratingAgency.label,
              body: ratingAgency.description,
              onOpen: handleOpen
            }
          ),
          rating && /* @__PURE__ */ jsx20(Text8, { variant: "small", className: "mobius-quote-card__ratings-value", children: rating })
        ] })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCard/Ratings/index.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var Ratings2 = (props) => {
  return /* @__PURE__ */ jsx21(Ratings, { ...props });
};

// src/components/QuoteCards/QuoteCard/Header/BindRestriction.tsx
import { Text as Text9 } from "@simplybusiness/mobius";
import { Fragment as Fragment4, jsx as jsx22 } from "react/jsx-runtime";
var BindRestriction = ({
  offlinePurchaseOnlyRfq,
  showBindRestrictions
}) => {
  if (!offlinePurchaseOnlyRfq) return null;
  return /* @__PURE__ */ jsx22(Fragment4, { children: showBindRestrictions ? /* @__PURE__ */ jsx22(Text9, { className: "mobius-quote-card__bind-disable", children: "Purchase by phone" }) : /* @__PURE__ */ jsx22(Text9, { className: "mobius-quote-card__bind-able", children: "Purchase online" }) });
};

// src/components/QuoteCards/QuoteCard/Header/Header.tsx
import { jsx as jsx23, jsxs as jsxs16 } from "react/jsx-runtime";
var Header = ({
  logo,
  insurerName,
  insurerFinancialStrengthRatings: ratings,
  offlinePurchaseOnly,
  offlinePurchaseOnlyText,
  offlinePurchaseOnlyNumber,
  offlinePurchaseOnlyRfq
}) => {
  const showRatings = ratings?.rating && ratings?.ratingType;
  const logoAltText = insurerName ? `${insurerName} insurer logo` : "Provider logo";
  const showBindRestrictions = !!offlinePurchaseOnly && !!offlinePurchaseOnlyText && !!offlinePurchaseOnlyNumber;
  return /* @__PURE__ */ jsxs16(
    Flex5,
    {
      flexDirection: "column",
      gap: "xxs",
      className: "mobius-quote-card__header",
      children: [
        /* @__PURE__ */ jsx23(
          BindRestriction,
          {
            offlinePurchaseOnlyRfq,
            showBindRestrictions
          }
        ),
        logo && /* @__PURE__ */ jsx23(
          "img",
          {
            src: logo,
            alt: logoAltText,
            className: "mobius-quote-card__header-logo"
          }
        ),
        showRatings && /* @__PURE__ */ jsx23(Ratings2, { data: ratings })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCard/Header/HeaderUK.tsx
import { Text as Text10 } from "@simplybusiness/mobius";
import { jsx as jsx24, jsxs as jsxs17 } from "react/jsx-runtime";
var HeaderUK = ({
  logo,
  insurerName,
  underWriterText
}) => {
  const logoAltText = insurerName ? `${insurerName} insurer logo` : "Provider logo";
  return /* @__PURE__ */ jsxs17("div", { className: "mobius-quote-card__header --is-uk", children: [
    logo && /* @__PURE__ */ jsx24("div", { className: "mobius-quote-card__header-logo --is-uk", children: /* @__PURE__ */ jsx24(
      "img",
      {
        src: logo,
        alt: logoAltText,
        className: "mobius-quote-card__header-logo --is-uk"
      }
    ) }),
    underWriterText && /* @__PURE__ */ jsxs17(Text10, { className: "header__underwriter-text", children: [
      /* @__PURE__ */ jsx24("span", { children: "Underwritten by:" }),
      /* @__PURE__ */ jsx24("br", {}),
      /* @__PURE__ */ jsx24("span", { children: underWriterText })
    ] })
  ] });
};

// src/components/QuoteCards/QuoteCard/NoQuoteCard/NoQuoteCard.tsx
import { Flex as Flex6, Text as Text11 } from "@simplybusiness/mobius";
import { jsx as jsx25, jsxs as jsxs18 } from "react/jsx-runtime";
var NoQuoteCard = ({ insurer, insurerLogo }) => {
  return /* @__PURE__ */ jsxs18(
    "div",
    {
      role: "listitem",
      className: "mobius-quote-card__no-quote",
      "data-testid": "no-quote-card",
      children: [
        /* @__PURE__ */ jsx25("div", { className: "mobius-quote-card__no-quote-logo", children: /* @__PURE__ */ jsx25(Logo, { logoPath: insurerLogo, alt: `${insurer} logo` }) }),
        /* @__PURE__ */ jsx25(Flex6, { alignItems: "center", className: "", children: /* @__PURE__ */ jsx25(Text11, { variant: "small", children: "Based on the details you've given us, this provider can't provide a quote" }) })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCard/Pricing/Pricing.tsx
import {
  calendarClock,
  chartMixedUpCircleDollar,
  circleDollar
} from "@simplybusiness/icons";
import { Flex as Flex8, Icon as Icon6, Text as Text14, useBreakpoint as useBreakpoint4 } from "@simplybusiness/mobius";
import { useAtom as useAtom6 } from "jotai";

// src/components/QuoteCards/QuoteCard/QuoteCardBadges/QuoteCardBadges.tsx
import classNames12 from "classnames";
import { jsx as jsx26 } from "react/jsx-runtime";
function QuoteCardBadges({ children, className }) {
  const quoteCardBadgesClasses = classNames12(
    "mobius-quote-card__badges",
    className
  );
  return /* @__PURE__ */ jsx26("div", { className: quoteCardBadgesClasses, "data-testid": "quote-card-badges", children });
}

// src/components/QuoteCards/QuoteCard/QuoteCardBadges/QuoteCardBadge.tsx
import classNames13 from "classnames";
import { jsx as jsx27, jsxs as jsxs19 } from "react/jsx-runtime";
function QuoteCardBadge({
  icon,
  children,
  "data-testid": dataTestId,
  className
}) {
  const quoteCardBadgeClasses = classNames13(
    "mobius-quote-card__badge",
    className
  );
  return /* @__PURE__ */ jsxs19("div", { className: quoteCardBadgeClasses, "data-testid": dataTestId, children: [
    icon && /* @__PURE__ */ jsx27("span", { className: "mobius-quote-card__badge-icon", children: icon }),
    children
  ] });
}

// src/components/QuoteCards/QuoteCard/Pricing/Premium.tsx
import { Flex as Flex7, Text as Text12 } from "@simplybusiness/mobius";
import { jsx as jsx28, jsxs as jsxs20 } from "react/jsx-runtime";
var Premium = (props) => {
  const {
    isMonthly,
    hasInstallments,
    installmentsAmount,
    premium,
    offlinePurchaseOnly
  } = props;
  const primaryAmountClasses = "mobius-quote-card__pricing-primary-amount";
  const periodSuffixClasses = "mobius-quote-card__pricing-period-suffix";
  if (!isMonthly) {
    return /* @__PURE__ */ jsxs20(Flex7, { alignItems: "baseline", children: [
      /* @__PURE__ */ jsx28(Text12, { className: primaryAmountClasses, "data-testid": "premium-amount", children: premium }),
      /* @__PURE__ */ jsx28(
        Text12,
        {
          elementType: "span",
          variant: "small",
          className: periodSuffixClasses,
          children: " / year"
        }
      )
    ] });
  }
  return hasInstallments && installmentsAmount ? /* @__PURE__ */ jsxs20(Flex7, { alignItems: "baseline", children: [
    /* @__PURE__ */ jsx28(Text12, { className: primaryAmountClasses, "data-testid": "installment-amount", children: installmentsAmount }),
    /* @__PURE__ */ jsxs20(Text12, { className: periodSuffixClasses, elementType: "span", variant: "small", children: [
      "/ month ",
      offlinePurchaseOnly && " (estimated)"
    ] })
  ] }) : /* @__PURE__ */ jsxs20(Flex7, { flexDirection: "column", children: [
    /* @__PURE__ */ jsx28(
      Text12,
      {
        variant: "small",
        className: "mobius-quote-card__pricing-unavailable-installment",
        children: "Monthly installments not available"
      }
    ),
    /* @__PURE__ */ jsxs20("div", { "data-testid": "unavailable-monthly-installment-amount", children: [
      /* @__PURE__ */ jsx28(Text12, { elementType: "span", className: primaryAmountClasses, children: premium }),
      /* @__PURE__ */ jsx28(
        Text12,
        {
          elementType: "span",
          variant: "small",
          className: periodSuffixClasses,
          children: " / year"
        }
      )
    ] })
  ] });
};

// src/components/QuoteCards/QuoteCard/Pricing/OfflinePurchaseText.tsx
import { Text as Text13, Link as Link6 } from "@simplybusiness/mobius";
import { jsx as jsx29, jsxs as jsxs21 } from "react/jsx-runtime";
var OfflinePurchaseText = ({
  offlinePurchaseOnlyText,
  offlinePurchaseOnlyNumber,
  isMobile
}) => /* @__PURE__ */ jsxs21(
  Text13,
  {
    "data-testid": "pricing-sub-text",
    className: "mobius-quote-card__bind-sub-text",
    children: [
      offlinePurchaseOnlyText,
      " \xA0",
      isMobile ? /* @__PURE__ */ jsx29(Link6, { href: `tel:${offlinePurchaseOnlyNumber}`, children: offlinePurchaseOnlyNumber }) : /* @__PURE__ */ jsx29(Text13, { className: "mobius-quote-card__bind-phone-number", children: offlinePurchaseOnlyNumber })
    ]
  }
);

// src/components/QuoteCards/QuoteCard/Pricing/Pricing.tsx
import { Fragment as Fragment5, jsx as jsx30, jsxs as jsxs22 } from "react/jsx-runtime";
var Pricing = ({
  quote,
  displayVariant,
  displayBadges,
  showInstallmentsInformation
}) => {
  const {
    installmentsAvailable,
    downpaymentAmount,
    installmentsAmount,
    installmentsCount,
    installmentsTotal,
    premium,
    externalPaymentOptions
  } = quote;
  const [paymentPeriod] = useAtom6(paymentToggleAtom);
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const showZeroInterestBadge = hasInstallments && isMonthly && !externalPaymentOptions && !quote.offlinePurchaseOnly;
  const premiumEqualsInstallments = installmentsTotal === premium;
  const showTotalCostBadge = hasInstallments && !isMonthly && premiumEqualsInstallments;
  const { processingFee } = quote;
  const amountDueClasses = "mobius-quote-card__pricing-amount-due";
  const showInstalments = isMonthly && hasInstallments && showInstallmentsInformation;
  const { down } = useBreakpoint4();
  const isMobile = down("md");
  const displayOfflinePurchaseText = displayVariant != "qds" && quote.offlinePurchaseOnly && quote.offlinePurchaseOnlyRfq;
  let installmentPremiumToShow;
  if (externalPaymentOptions && installmentsTotal) {
    installmentPremiumToShow = installmentsTotal;
  } else if (isMonthly && installmentsTotal) {
    installmentPremiumToShow = installmentsTotal;
  } else {
    installmentPremiumToShow = premium;
  }
  return /* @__PURE__ */ jsx30(Flex8, { flexDirection: "column", className: "mobius-quote-card__pricing", children: /* @__PURE__ */ jsxs22(Flex8, { flexDirection: "column", children: [
    /* @__PURE__ */ jsxs22(Fragment5, { children: [
      /* @__PURE__ */ jsx30(Flex8, { alignItems: "baseline", gap: "xxs", children: /* @__PURE__ */ jsx30(
        Premium,
        {
          isMonthly,
          hasInstallments,
          installmentsAmount,
          premium,
          offlinePurchaseOnly: quote.offlinePurchaseOnly
        }
      ) }),
      showInstalments && /* @__PURE__ */ jsxs22(Fragment5, { children: [
        !quote.offlinePurchaseOnly && /* @__PURE__ */ jsxs22(Fragment5, { children: [
          /* @__PURE__ */ jsxs22(Text14, { variant: "small", className: amountDueClasses, children: [
            "Due today ",
            downpaymentAmount
          ] }),
          /* @__PURE__ */ jsxs22(Text14, { variant: "small", className: amountDueClasses, children: [
            "Then ",
            installmentsCount,
            " monthly installments of",
            " ",
            installmentsAmount
          ] })
        ] }),
        /* @__PURE__ */ jsx30(Flex8, { gap: "xxs", className: "mobius-quote-card__pricing-total", children: /* @__PURE__ */ jsxs22(
          Text14,
          {
            "data-testid": "pricing__pricing-total-amount",
            className: "mobius-quote-card__pricing-total-amount",
            elementType: "span",
            variant: "small",
            children: [
              "Total ",
              installmentPremiumToShow,
              quote.offlinePurchaseOnly && " (estimated)"
            ]
          }
        ) })
      ] }),
      processingFee && /* @__PURE__ */ jsx30(Flex8, { gap: "xxs", className: "mobius-quote-card__pricing-total", children: /* @__PURE__ */ jsx30(
        Text14,
        {
          "data-testid": "pricing__processing-fees",
          elementType: "span",
          variant: "small",
          children: `Inc. ${processingFee} processing fee`
        }
      ) })
    ] }),
    displayOfflinePurchaseText && /* @__PURE__ */ jsx30(
      OfflinePurchaseText,
      {
        offlinePurchaseOnlyText: quote.offlinePurchaseOnlyText,
        offlinePurchaseOnlyNumber: quote.offlinePurchaseOnlyNumber,
        isMobile
      }
    ),
    displayBadges && /* @__PURE__ */ jsxs22(QuoteCardBadges, { children: [
      /* @__PURE__ */ jsx30(
        QuoteCardBadge,
        {
          icon: /* @__PURE__ */ jsx30(
            Icon6,
            {
              icon: calendarClock,
              title: "Policy duration information"
            }
          ),
          "data-testid": "quote-card-badge-12-month-policy",
          children: "12 month policy"
        }
      ),
      showZeroInterestBadge && /* @__PURE__ */ jsx30(
        QuoteCardBadge,
        {
          icon: /* @__PURE__ */ jsx30(
            Icon6,
            {
              icon: chartMixedUpCircleDollar,
              title: "Zero interest payment option"
            }
          ),
          "data-testid": "quote-card-badge-zero-interest",
          children: "Zero interest"
        }
      ),
      showTotalCostBadge && /* @__PURE__ */ jsx30(
        QuoteCardBadge,
        {
          icon: /* @__PURE__ */ jsx30(
            Icon6,
            {
              icon: circleDollar,
              title: "Cost comparison information"
            }
          ),
          "data-testid": "quote-card-badge-annual-cost-same-as-monthly",
          children: "Total annual cost same as monthly"
        }
      )
    ] })
  ] }) });
};

// src/components/QuoteCards/QuoteCard/Pricing/index.tsx
import { jsx as jsx31 } from "react/jsx-runtime";
var Pricing2 = (props) => {
  return /* @__PURE__ */ jsx31(Pricing, { ...props });
};

// src/components/QuoteCards/QuoteCard/PricingUK/Pricing.tsx
import { calendarClock as calendarClock2 } from "@simplybusiness/icons";
import { Flex as Flex11, Icon as Icon7, Text as Text17 } from "@simplybusiness/mobius";
import { useAtom as useAtom7 } from "jotai";

// src/components/QuoteCards/QuoteCard/PricingUK/MonthlyPremium.tsx
import { Flex as Flex9, Text as Text15 } from "@simplybusiness/mobius";
import { jsx as jsx32, jsxs as jsxs23 } from "react/jsx-runtime";
var MonthlyPremium = ({ installmentsAmount }) => {
  return /* @__PURE__ */ jsxs23(Flex9, { alignItems: "baseline", flexWrap: "wrap", columnGap: "xxs", children: [
    /* @__PURE__ */ jsx32(
      Text15,
      {
        className: "mobius-quote-card__pricing-primary-amount --is-uk",
        "data-testid": "installment-amount",
        children: installmentsAmount
      }
    ),
    /* @__PURE__ */ jsx32(
      Text15,
      {
        className: "mobius-quote-card__pricing-period-suffix --is-uk",
        elementType: "span",
        variant: "small",
        children: "/\xA0month"
      }
    )
  ] });
};

// src/components/QuoteCards/QuoteCard/PricingUK/AnnualPremium.tsx
import { Flex as Flex10, Text as Text16 } from "@simplybusiness/mobius";
import { jsx as jsx33, jsxs as jsxs24 } from "react/jsx-runtime";
var AnnualPremium = ({ premium, message }) => {
  return /* @__PURE__ */ jsxs24(Flex10, { alignItems: "baseline", flexWrap: "wrap", columnGap: "xxs", children: [
    message && /* @__PURE__ */ jsx33(
      Text16,
      {
        variant: "small",
        className: "mobius-quote-card__pricing-unavailable-installment",
        children: message
      }
    ),
    /* @__PURE__ */ jsxs24(
      Flex10,
      {
        "data-testid": "unavailable-monthly-installment-amount",
        alignItems: "baseline",
        columnGap: "xxs",
        children: [
          /* @__PURE__ */ jsx33(
            Text16,
            {
              elementType: "span",
              className: "mobius-quote-card__pricing-primary-amount --is-uk",
              children: premium
            }
          ),
          /* @__PURE__ */ jsx33(
            Text16,
            {
              elementType: "span",
              variant: "small",
              className: "mobius-quote-card__pricing-period-suffix --is-uk",
              children: "/\xA0annual"
            }
          )
        ]
      }
    )
  ] });
};

// src/components/QuoteCards/QuoteCard/PricingUK/Premium.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var Premium2 = (props) => {
  const { isMonthly, hasInstallments, installmentsAmount, premium } = props;
  if (!isMonthly) {
    return /* @__PURE__ */ jsx34(AnnualPremium, { premium });
  }
  return hasInstallments && installmentsAmount ? /* @__PURE__ */ jsx34(MonthlyPremium, { installmentsAmount }) : /* @__PURE__ */ jsx34(
    AnnualPremium,
    {
      premium,
      message: "Monthly instalments not available"
    }
  );
};

// src/components/QuoteCards/QuoteCard/PricingUK/Pricing.tsx
import { Fragment as Fragment6, jsx as jsx35, jsxs as jsxs25 } from "react/jsx-runtime";
var Pricing3 = ({
  quote,
  showInstallmentsInformation
}) => {
  const {
    installmentsAvailable,
    downpaymentAmount,
    installmentsAmount,
    installmentsCount,
    installmentsTotal,
    premium
  } = quote;
  const [paymentPeriod] = useAtom7(paymentToggleAtom);
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const showInstalments = isMonthly && hasInstallments && showInstallmentsInformation;
  const instalmentsText = downpaymentAmount ? `Then ${installmentsCount} monthly payments of` : `${installmentsCount} monthly payments of`;
  return /* @__PURE__ */ jsxs25("div", { className: "mobius-quote-card__pricing --is-uk", children: [
    /* @__PURE__ */ jsxs25(Flex11, { alignItems: "flex-start", gap: "xxs", justifyContent: "space-between", children: [
      /* @__PURE__ */ jsx35(
        Premium2,
        {
          isMonthly,
          hasInstallments,
          installmentsAmount,
          premium
        }
      ),
      /* @__PURE__ */ jsx35(QuoteCardBadges, { className: "--is-uk --is-desktop", children: /* @__PURE__ */ jsx35(
        QuoteCardBadge,
        {
          icon: /* @__PURE__ */ jsx35(
            Icon7,
            {
              icon: calendarClock2,
              color: "var(--color-accent)",
              title: "Policy duration information"
            }
          ),
          "data-testid": "quote-card-badge-12-month-policy",
          children: "12 month policy"
        }
      ) })
    ] }),
    showInstalments && /* @__PURE__ */ jsxs25("div", { className: "mobius-quote-card__instalments-grid", children: [
      downpaymentAmount && /* @__PURE__ */ jsxs25(Fragment6, { children: [
        /* @__PURE__ */ jsx35(Text17, { children: "First payment" }),
        /* @__PURE__ */ jsx35(Text17, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx35("strong", { children: downpaymentAmount }) })
      ] }),
      /* @__PURE__ */ jsx35(Text17, { children: instalmentsText }),
      /* @__PURE__ */ jsx35(Text17, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx35("strong", { children: installmentsAmount }) }),
      /* @__PURE__ */ jsx35(Text17, { children: "Total over 12 months" }),
      /* @__PURE__ */ jsx35(Text17, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx35("strong", { children: installmentsTotal }) })
    ] }),
    /* @__PURE__ */ jsx35(QuoteCardBadges, { className: "--is-mobile", children: /* @__PURE__ */ jsx35(
      QuoteCardBadge,
      {
        icon: /* @__PURE__ */ jsx35(
          Icon7,
          {
            icon: calendarClock2,
            color: "var(--color-accent)",
            title: "Policy duration information"
          }
        ),
        "data-testid": "quote-card-badge-12-month-policy",
        children: "12 month policy"
      }
    ) })
  ] });
};

// src/components/QuoteCards/QuoteCard/QuoteActions/ActionsWithoutQdpDrawer.tsx
import { Button as Button5 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext8 } from "@simplybusiness/services";

// src/utils/storybook/isStorybook.ts
function isStorybook() {
  return "__STORYBOOK_ADDONS_CHANNEL__" in globalThis;
}

// src/components/QuoteCards/QuoteCard/QuoteActions/ActionsWithoutQdpDrawer.tsx
import { jsx as jsx36, jsxs as jsxs26 } from "react/jsx-runtime";
var ActionsWithoutQdpDrawer = ({ quote }) => {
  const { snowplow } = useSnowplowContext8();
  const tokens = useTokensValues();
  if (!tokens) {
    return null;
  }
  function getSubmitterName(e) {
    const nativeEvent = e.nativeEvent;
    const submitter = nativeEvent.submitter;
    return submitter?.name;
  }
  function handleSubmit(e) {
    const submitterName = getSubmitterName(e);
    const snowplowEventName = {
      details_button_clicked: "detailsClicked",
      buy_button_clicked: "selectClicked"
    }[submitterName];
    if (snowplowEventName) {
      snowplow?.trigger(snowplowEventName);
    }
    if (isStorybook()) {
      e.preventDefault();
    }
  }
  return /* @__PURE__ */ jsx36(
    "form",
    {
      method: "POST",
      onSubmit: handleSubmit,
      className: "mobius-quote-actions-form",
      children: /* @__PURE__ */ jsxs26("div", { className: "mobius-quote-actions", children: [
        /* @__PURE__ */ jsx36(
          "input",
          {
            type: "hidden",
            name: "authenticity_token",
            value: tokens.authenticityToken
          }
        ),
        /* @__PURE__ */ jsx36("input", { type: "hidden", name: "journey_token", value: tokens.journeyToken }),
        /* @__PURE__ */ jsx36("input", { type: "hidden", name: "quote_id", value: quote.id }),
        /* @__PURE__ */ jsx36(
          "input",
          {
            type: "hidden",
            name: "insurer",
            value: quote.covers[0]?.coverInsurer
          }
        ),
        /* @__PURE__ */ jsx36(
          Button5,
          {
            type: "submit",
            variant: "secondary",
            className: "mobius-quote-actions__button mobius-quote-actions__button--is-details",
            name: "details_button_clicked",
            value: "yes",
            size: "sm",
            children: "Details"
          }
        ),
        /* @__PURE__ */ jsx36(
          Button5,
          {
            type: "submit",
            name: "buy_button_clicked",
            value: "yes",
            className: "mobius-quote-actions__button mobius-quote-actions__button--is-select",
            size: "sm",
            children: "Buy"
          }
        )
      ] })
    }
  );
};

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteActions.tsx
import { Accordion as Accordion3, Button as Button7, Drawer, TextOrHTML } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext10 } from "@simplybusiness/services";
import classNames16 from "classnames";
import { useAtom as useAtom8, useSetAtom } from "jotai";
import { useRef, useState as useState3 } from "react";

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/AdditionalCoverageInformation/AdditionalCoverageInformation.tsx
import { Box as Box2, Text as Text18 } from "@simplybusiness/mobius";
import { Fragment as Fragment7, jsx as jsx37, jsxs as jsxs27 } from "react/jsx-runtime";
var renderedLayout = (items) => {
  if (items.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs27(
    Box2,
    {
      className: "mobius-additional-coverage-info --has-divider",
      "data-testid": "additional-coverage-info",
      children: [
        /* @__PURE__ */ jsx37(Text18, { className: "mobius-additional-coverage-info__title", elementType: "h3", children: "Additional Coverage Information" }),
        /* @__PURE__ */ jsx37("ul", { children: items.map((item, index) => /* @__PURE__ */ jsx37("li", { children: /* @__PURE__ */ jsx37("p", { className: "mobius-additional-coverage-info__text", children: item }) }, index)) })
      ]
    }
  );
};
var AdditionalCoverageInformation = ({
  vertical,
  additionalDisclosures
}) => {
  const additionalDisclosuresInformation = () => {
    if (vertical === "workers_compensation") {
      return Object.entries(additionalDisclosures || {}).map(
        ([key, value]) => /* @__PURE__ */ jsxs27(Fragment7, { children: [
          /* @__PURE__ */ jsx37("strong", { children: camelToTitleCase(key) }),
          ": ",
          value
        ] })
      );
    }
    return Object.values(additionalDisclosures)[0];
  };
  return renderedLayout(additionalDisclosuresInformation());
};

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/Footer.tsx
import { Flex as Flex13 } from "@simplybusiness/mobius";

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/SubmissionForm/SubmissionForm.tsx
import { Button as Button6, Flex as Flex12 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext9 } from "@simplybusiness/services";
import classNames15 from "classnames";
import { useAtomValue } from "jotai";
import { useState as useState2 } from "react";

// src/components/QuoteCards/QuoteCard/quoteUnavailableForPurchaseModal.tsx
import { Modal } from "@simplybusiness/mobius";
import { jsx as jsx38, jsxs as jsxs28 } from "react/jsx-runtime";
var QuoteUnavailableForPurchase = ({
  modalOpen,
  onClose,
  insurerName
}) => /* @__PURE__ */ jsxs28(
  Modal,
  {
    isOpen: modalOpen,
    onClose,
    className: "mobius-quote-card__modal-container",
    animation: "fade",
    closeLabel: "Close",
    children: [
      /* @__PURE__ */ jsx38(Modal.Header, { children: "Sorry, this policy is only available to purchase during office hours." }),
      /* @__PURE__ */ jsxs28(Modal.Content, { children: [
        "Please come back during ",
        insurerName,
        "'s office hours, Mon-Fri 6:30am - 11pm (ET) or Sat-Sun 2pm - 8pm (ET) to finalize your purchase. We will email you a link shortly."
      ] })
    ]
  }
);

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/ReferredQuotesCallDisplay/ReferredQuotesCallDisplay.tsx
import { telephone } from "@simplybusiness/icons";
import { Icon as Icon8, LinkButton, useBreakpoint as useBreakpoint5, Text as Text19 } from "@simplybusiness/mobius";
import classNames14 from "classnames";
import { jsx as jsx39, jsxs as jsxs29 } from "react/jsx-runtime";
var ReferredQuotesCallDisplay = ({
  quote,
  displayVariant
}) => {
  const { down } = useBreakpoint5();
  const isMobile = down("md");
  const selectActionClasses = classNames14(
    "mobius-referred-quotes-call-display__button",
    "mobius-referred-quotes-call-displays__link-button"
  );
  const textClasses = classNames14(
    "mobius-referred-quotes-call-display__button",
    "mobius-referred-quotes-call-display__bind-sub-text"
  );
  return isMobile ? /* @__PURE__ */ jsxs29(
    LinkButton,
    {
      size: "sm",
      href: `tel:${quote.offlinePurchaseOnlyNumber}`,
      className: selectActionClasses,
      children: [
        /* @__PURE__ */ jsx39(Icon8, { icon: telephone, size: "xs" }),
        "\xA0Call to Buy"
      ]
    }
  ) : displayVariant === "qcp" ? /* @__PURE__ */ jsxs29(Text19, { "data-testid": "qcp-desktop-call-sub-text", className: textClasses, children: [
    "Call to Buy",
    /* @__PURE__ */ jsx39(Text19, { className: "mobius-referred-quotes-call-display__qcp-phone-number", children: quote.offlinePurchaseOnlyNumber })
  ] }) : /* @__PURE__ */ jsxs29(
    Text19,
    {
      "data-testid": "qds-desktop-call-sub-text",
      className: "mobius-quote-card__bind-sub-text",
      children: [
        quote.offlinePurchaseOnlyText,
        /* @__PURE__ */ jsx39(Text19, { className: "mobius-referred-quotes-call-display__qds-phone-number", children: quote.offlinePurchaseOnlyNumber })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/SubmissionForm/SubmissionForm.tsx
import { Fragment as Fragment8, jsx as jsx40, jsxs as jsxs30 } from "react/jsx-runtime";
var SubmissionForm = ({
  quote,
  hasInstallments
}) => {
  const { snowplow } = useSnowplowContext9();
  const [isPending, setIsPending] = useState2(false);
  const [isSuccess, setIsSuccess] = useState2(false);
  const [modalOpen, setModalOpen] = useState2(false);
  const paymentPeriod = useAtomValue(paymentToggleAtom);
  const tokens = useTokensValues();
  const handleSubmit = (event) => {
    setIsPending(true);
    snowplow?.trigger("nextStepsClicked");
    try {
      event.currentTarget.submit();
      setIsPending(false);
      setIsSuccess(true);
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error(JSON.stringify({ error }));
      }
    }
  };
  const openModal = () => {
    setModalOpen(true);
  };
  const closeModal = () => {
    setModalOpen(false);
  };
  const handleButtonClick = () => {
    if (quote.quoteAvailableForPurchase === false) {
      openModal();
    } else {
      document.getElementById(`form-${quote.id}`)?.submit();
    }
  };
  const actionsButtonClasses = classNames15(
    "mobius-quote-actions__button",
    // `qa-buy-quote-option` class is used in cypress tests
    // keeping it to comply with shakedowns
    "qa-buy-quote-option",
    "mobius-quote-actions__link-button"
  );
  const hiddenInputs = [
    {
      id: "authenticity_token",
      name: "authenticity_token",
      value: tokens?.authenticityToken || ""
    },
    {
      id: "journey_token",
      name: "journey_token",
      value: tokens?.journeyToken || ""
    },
    {
      id: "quote_id",
      name: "quote_id",
      value: quote.id
    },
    {
      id: "insurer",
      name: "insurer",
      value: quote.covers[0]?.coverInsurer
    },
    {
      id: "payment_period",
      name: "payment_period",
      value: hasInstallments ? paymentPeriod : "annual"
    },
    {
      id: "buy_button_clicked",
      name: "buy_button_clicked",
      value: "yes"
    },
    {
      id: "rfq_id",
      name: "rfq_id",
      value: quote.rfqId
    }
  ];
  return /* @__PURE__ */ jsxs30(Fragment8, { children: [
    /* @__PURE__ */ jsxs30(
      "form",
      {
        id: `form-${quote.id}`,
        method: "POST",
        onSubmit: handleSubmit,
        "data-testid": `form-${quote.id}`,
        children: [
          hiddenInputs.map((el) => /* @__PURE__ */ jsx40(
            "input",
            {
              "data-testid": el.id,
              id: el.id,
              type: "hidden",
              value: el.value,
              name: el.name,
              onChange: () => {
              }
            },
            el.id
          )),
          /* @__PURE__ */ jsx40(
            Flex12,
            {
              className: "mobius-quote-actions",
              justifyContent: "space-between",
              gap: "sm",
              children: quote.offlinePurchaseOnly ? /* @__PURE__ */ jsx40(ReferredQuotesCallDisplay, { quote, displayVariant: "qds" }) : /* @__PURE__ */ jsx40(
                Button6,
                {
                  id: `select-${quote.id}`,
                  className: actionsButtonClasses,
                  size: "sm",
                  type: "button",
                  isDisabled: isPending || isSuccess,
                  onClick: () => handleButtonClick(),
                  children: isPending || isSuccess ? "Please wait..." : "Next step"
                }
              )
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx40(
      QuoteUnavailableForPurchase,
      {
        modalOpen,
        onClose: closeModal,
        insurerName: quote.covers[0]?.coverInsurer
      }
    )
  ] });
};

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/Footer.tsx
import { jsx as jsx41, jsxs as jsxs31 } from "react/jsx-runtime";
var Footer = ({
  quote,
  paymentInformationRef,
  hasInstallments
}) => {
  function scrollToPaymentInformation() {
    paymentInformationRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
      inline: "center"
    });
  }
  const showPaymentInformation = !quote.offlinePurchaseOnly;
  return /* @__PURE__ */ jsxs31(
    Flex13,
    {
      className: "mobius-quote-details-drawer__footer",
      flexDirection: "column",
      children: [
        /* @__PURE__ */ jsxs31(Flex13, { className: "mobius-quote-details-drawer__footer-payment-info", children: [
          hasInstallments && /* @__PURE__ */ jsx41(PaymentToggle, { "data-test-id": "footer-payment-toggle" }),
          /* @__PURE__ */ jsx41(Pricing2, { quote, displayVariant: "qds" })
        ] }),
        showPaymentInformation && /* @__PURE__ */ jsx41(
          "button",
          {
            className: "mobius-quote-details-drawer__footer-payment-info-link",
            onClick: () => scrollToPaymentInformation(),
            children: "See payment information"
          }
        ),
        /* @__PURE__ */ jsx41(SubmissionForm, { quote, hasInstallments })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/PaymentInformation/index.tsx
import { forwardRef as forwardRef5 } from "react";

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/PaymentInformation/PaymentInformation.tsx
import { forwardRef as forwardRef4 } from "react";
import {
  calendarClock as calendarClock3,
  chartMixedUpCircleDollar as chartMixedUpCircleDollar2,
  circleDollar as circleDollar2
} from "@simplybusiness/icons";
import { Box as Box3, Flex as Flex14, Icon as Icon9, Text as Text20, Segment } from "@simplybusiness/mobius";
import { Fragment as Fragment9, jsx as jsx42, jsxs as jsxs32 } from "react/jsx-runtime";
var PaymentInformation = forwardRef4(
  (props, ref) => {
    const {
      quote: {
        installmentsAvailable,
        downpaymentAmount,
        installmentsAmount,
        installmentsCount,
        installmentsTotal,
        premium,
        processingFee,
        covers,
        multiProductPremiums,
        insurerLogo,
        policyStartDate,
        externalPaymentOptions,
        primaryProductTitle,
        installmentFeesApplied
      },
      paymentPeriod = "monthly"
    } = props;
    const isMonthly = paymentPeriod === "monthly";
    const hasInstallments = installmentsAvailable && installmentsAmount;
    const showZeroInterestBadge = hasInstallments && isMonthly && !externalPaymentOptions;
    const premiumEqualsInstallments = !externalPaymentOptions || externalPaymentOptions && premium === installmentsTotal;
    const showTotalCostBadge = hasInstallments && !isMonthly && premiumEqualsInstallments;
    const productTitle = primaryProductTitle || transformCoverage(covers)[0]?.displayName;
    const totalPrice = isMonthly ? installmentsTotal : premium;
    const quoteCardBadgeText = installmentFeesApplied ? "Pay no installment fee with annual" : "Total annual cost same as monthly";
    const processingFeeDetailsText = processingFee ? `(inc. tax + ${processingFee} processing fee)` : "(inc. tax + fees)";
    return /* @__PURE__ */ jsxs32(
      Box3,
      {
        className: "mobius-payment-info --has-divider",
        ref,
        "data-testid": "payment-information",
        children: [
          /* @__PURE__ */ jsx42(Text20, { className: "mobius-payment-info__title", variant: "h3", children: "Payment Information" }),
          /* @__PURE__ */ jsxs32(QuoteCardBadges, { children: [
            /* @__PURE__ */ jsx42(
              QuoteCardBadge,
              {
                icon: /* @__PURE__ */ jsx42(Icon9, { icon: calendarClock3, title: "Policy duration information" }),
                "data-testid": "quote-card-badge-12-month-policy",
                children: "12 month policy"
              }
            ),
            showZeroInterestBadge && /* @__PURE__ */ jsx42(
              QuoteCardBadge,
              {
                icon: /* @__PURE__ */ jsx42(
                  Icon9,
                  {
                    icon: chartMixedUpCircleDollar2,
                    title: "Zero interest payment option"
                  }
                ),
                "data-testid": "quote-card-badge-zero-interest",
                children: "Zero interest"
              }
            ),
            showTotalCostBadge && /* @__PURE__ */ jsx42(
              QuoteCardBadge,
              {
                icon: /* @__PURE__ */ jsx42(Icon9, { icon: circleDollar2, title: "Cost comparison information" }),
                "data-testid": "quote-card-badge-annual-cost-same-as-monthly",
                children: quoteCardBadgeText
              }
            )
          ] }),
          multiProductPremiums ? /* @__PURE__ */ jsxs32(Fragment9, { children: [
            /* @__PURE__ */ jsx42(
              ProductInfo,
              {
                logo: insurerLogo,
                productTitle: "General Liability",
                premium: multiProductPremiums?.generalLiability,
                policyStartDate
              }
            ),
            /* @__PURE__ */ jsx42(
              ProductInfo,
              {
                logo: insurerLogo,
                productTitle: "Professional Liability",
                premium: multiProductPremiums?.professionalLiability,
                policyStartDate
              }
            )
          ] }) : /* @__PURE__ */ jsx42(
            ProductInfo,
            {
              logo: insurerLogo,
              productTitle,
              premium: totalPrice,
              policyStartDate
            }
          ),
          /* @__PURE__ */ jsx42(Flex14, { flexDirection: "column", children: /* @__PURE__ */ jsxs32("div", { className: "mobius-payment-info__total-outer-container", children: [
            /* @__PURE__ */ jsxs32(Fragment9, { children: [
              /* @__PURE__ */ jsxs32(
                Flex14,
                {
                  justifyContent: "space-between",
                  gap: "xs",
                  className: "mobius-payment-info__total-today",
                  flexGrow: 1,
                  children: [
                    /* @__PURE__ */ jsx42("span", { children: "To pay today" }),
                    /* @__PURE__ */ jsx42("span", { children: isMonthly && hasInstallments ? downpaymentAmount : premium })
                  ]
                }
              ),
              isMonthly && hasInstallments && /* @__PURE__ */ jsxs32(
                Flex14,
                {
                  justifyContent: "space-between",
                  gap: "xs",
                  className: "mobius-payment-info__installments",
                  flexGrow: 1,
                  children: [
                    /* @__PURE__ */ jsxs32("span", { children: [
                      installmentsCount,
                      " monthly payments of"
                    ] }),
                    /* @__PURE__ */ jsx42("span", { children: installmentsAmount })
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsx42(Segment, { colour: "subtle", className: "mobius-payment-info__total", children: /* @__PURE__ */ jsxs32(Flex14, { justifyContent: "space-between", gap: "xs", flexGrow: 1, children: [
              /* @__PURE__ */ jsxs32("span", { children: [
                /* @__PURE__ */ jsx42("strong", { children: "Total" }),
                " ",
                processingFeeDetailsText
              ] }),
              /* @__PURE__ */ jsx42("span", { children: /* @__PURE__ */ jsx42("strong", { "data-testid": "payment-information-total", children: isMonthly && installmentsTotal ? installmentsTotal : premium }) })
            ] }) })
          ] }) })
        ]
      }
    );
  }
);
PaymentInformation.displayName = "PaymentInformation";

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteDetails/PaymentInformation/index.tsx
import { jsx as jsx43 } from "react/jsx-runtime";
var PaymentInformation2 = forwardRef5(
  (props, ref) => {
    return /* @__PURE__ */ jsx43(PaymentInformation, { ...props, ref });
  }
);
PaymentInformation2.displayName = "PaymentInformation";

// src/components/QuoteCards/QuoteCard/QuoteActions/QuoteActions.tsx
import { Fragment as Fragment10, jsx as jsx44, jsxs as jsxs33 } from "react/jsx-runtime";
var QuoteActions = ({ quote }) => {
  const { snowplow } = useSnowplowContext10();
  const [paymentPeriod] = useAtom8(paymentToggleAtom);
  const [drawerOpen, setDrawerOpen] = useState3(false);
  const setSliderIsOpen = useSetAtom(sliderStateAtom);
  const {
    installmentsAvailable,
    installmentsAmount,
    covers,
    vertical,
    additionalDisclosures,
    insurerLogo,
    ratings,
    insurerDetails,
    upsells,
    helpText,
    offlinePurchaseOnly
  } = quote;
  const extraCovers = transformExtraCoverage(covers);
  const hasInstallments = Boolean(installmentsAvailable && installmentsAmount);
  const detailsActionClasses = classNames16(
    "mobius-quote-actions__button",
    "mobius-quote-actions__button--is-details",
    "qa-view-details-option"
  );
  const selectActionClasses = classNames16(
    "mobius-quote-actions__button",
    "mobius-quote-actions__button--is-select",
    "qa-buy-quote-option"
  );
  const [detailsLabel, selectLabel] = ["Details", "Select"];
  const openDrawer = (label) => {
    setDrawerOpen(true);
    setSliderIsOpen(true);
    snowplow?.trigger("sliderOpened", { label });
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setSliderIsOpen(false);
  };
  const showPaymentInformation = !offlinePurchaseOnly;
  const paymentInformationRef = useRef(null);
  const handleInsurerDetailsOpen = () => {
    snowplow?.trigger("insurerDetailsAccordionClicked");
  };
  return /* @__PURE__ */ jsxs33(Fragment10, { children: [
    /* @__PURE__ */ jsxs33("div", { className: "mobius-quote-actions", children: [
      /* @__PURE__ */ jsx44(
        Button7,
        {
          variant: "secondary",
          className: detailsActionClasses,
          size: "sm",
          onClick: () => openDrawer(detailsLabel),
          children: detailsLabel
        }
      ),
      quote.offlinePurchaseOnly ? /* @__PURE__ */ jsx44(ReferredQuotesCallDisplay, { quote, displayVariant: "qcp" }) : /* @__PURE__ */ jsx44(
        Button7,
        {
          className: selectActionClasses,
          size: "sm",
          onClick: () => openDrawer(selectLabel),
          children: selectLabel
        }
      )
    ] }),
    /* @__PURE__ */ jsxs33(
      Drawer,
      {
        isOpen: drawerOpen,
        onClose: closeDrawer,
        className: "mobius-quote-details__drawer",
        closeLabel: "Close",
        direction: "right",
        children: [
          /* @__PURE__ */ jsx44(Drawer.Header, { children: /* @__PURE__ */ jsx44(Logo, { logoPath: insurerLogo }) }),
          /* @__PURE__ */ jsxs33(Drawer.Content, { children: [
            ratings && /* @__PURE__ */ jsx44(Ratings2, { data: ratings }),
            insurerDetails && /* @__PURE__ */ jsx44(
              Accordion3,
              {
                className: "mobius-quote-details__insurer-container",
                showText: "See more",
                hideText: "See less",
                onOpen: handleInsurerDetailsOpen,
                children: /* @__PURE__ */ jsx44(
                  TextOrHTML,
                  {
                    htmlClassName: "mobius-quote-details__insurer",
                    htmlElementType: "div",
                    text: insurerDetails
                  }
                )
              }
            ),
            covers && /* @__PURE__ */ jsx44(
              Coverage,
              {
                helpText,
                covers,
                extraCovers,
                upsells,
                displayVariant: "qds",
                className: "--has-divider",
                withModal: true
              }
            ),
            additionalDisclosures && Object.keys(additionalDisclosures).length > 0 && /* @__PURE__ */ jsx44(
              AdditionalCoverageInformation,
              {
                vertical,
                additionalDisclosures
              }
            ),
            showPaymentInformation && /* @__PURE__ */ jsx44(
              PaymentInformation2,
              {
                quote,
                paymentPeriod,
                ref: paymentInformationRef
              }
            )
          ] }),
          /* @__PURE__ */ jsx44(
            Footer,
            {
              quote,
              paymentInformationRef,
              hasInstallments
            }
          )
        ]
      }
    )
  ] });
};

// src/components/QuoteCards/QuoteCard/QuoteActions/index.tsx
import { jsx as jsx45 } from "react/jsx-runtime";
var QuoteActions2 = ({ quote, variant }) => {
  const isUKVariant = variants(variant);
  if (isUKVariant) {
    return /* @__PURE__ */ jsx45(ActionsWithoutQdpDrawer, { quote });
  }
  return /* @__PURE__ */ jsx45(QuoteActions, { quote });
};

// src/components/QuoteCards/QuoteCard/QuoteCard.tsx
import { Fragment as Fragment11, jsx as jsx46, jsxs as jsxs34 } from "react/jsx-runtime";
var QuoteCard = ({
  quote,
  variant,
  hideNoQuoteCard
}) => {
  const { status, helpText } = quote;
  const insurer = quote.covers[0]?.coverInsurer;
  const insurerParametrized = insurer?.toLowerCase().replaceAll(" ", "-");
  const chopinTestId = `buy-${insurerParametrized}-block`;
  const { isUK } = useLocale();
  const filteredCovers = filterCovers(quote);
  const quoteCardClass = "mobius-quote-card";
  if (status === "referred") {
    if (hideNoQuoteCard === true) {
      return /* @__PURE__ */ jsx46(Fragment11, {});
    }
    return /* @__PURE__ */ jsx46(NoQuoteCard, { insurer, insurerLogo: quote.insurerLogo });
  }
  return /* @__PURE__ */ jsxs34(
    "div",
    {
      role: "listitem",
      className: quoteCardClass,
      id: chopinTestId,
      "data-testid": "quote-cards-list-item",
      children: [
        isUK ? /* @__PURE__ */ jsx46(
          HeaderUK,
          {
            logo: quote.insurerLogo,
            insurerName: quote.insurerNameParametrized,
            underWriterText: quote.underwriter
          }
        ) : /* @__PURE__ */ jsx46(
          Header,
          {
            logo: quote.insurerLogo,
            insurerName: quote.insurerNameParametrized,
            insurerFinancialStrengthRatings: quote.ratings,
            offlinePurchaseOnly: quote.offlinePurchaseOnly,
            offlinePurchaseOnlyText: quote.offlinePurchaseOnlyText,
            offlinePurchaseOnlyNumber: quote.offlinePurchaseOnlyNumber,
            offlinePurchaseOnlyRfq: quote.offlinePurchaseOnlyRfq
          }
        ),
        isUK ? /* @__PURE__ */ jsx46(Pricing3, { quote, displayBadges: true, showInstallmentsInformation: true }) : /* @__PURE__ */ jsx46(Pricing2, { quote, displayBadges: true, showInstallmentsInformation: true }),
        /* @__PURE__ */ jsx46(
          Coverage,
          {
            helpText,
            covers: filteredCovers,
            additionalCoverage: quote.additionalCoverage,
            displayVariant: "qcp",
            withModal: true,
            variant,
            upsells: quote.upsells
          }
        ),
        /* @__PURE__ */ jsx46(QuoteActions2, { quote, variant })
      ]
    }
  );
};

// src/components/QuoteCards/QuoteCards.tsx
import { useAtom as useAtom9 } from "jotai";
import classNames17 from "classnames/dedupe";
import { useBreakpoint as useBreakpoint6 } from "@simplybusiness/mobius";

// src/components/QuoteCards/NoQuotesCard/NoQuotesCard.tsx
import { Text as Text21 } from "@simplybusiness/mobius";
import { jsx as jsx47, jsxs as jsxs35 } from "react/jsx-runtime";
function NoQuotesCard() {
  return /* @__PURE__ */ jsx47(
    "div",
    {
      role: "listitem",
      className: "mobius-no-quotes-card",
      "data-testid": "no-quotes-card",
      children: /* @__PURE__ */ jsxs35("div", { className: "mobius-no-quotes-card__body", children: [
        /* @__PURE__ */ jsx47(Text21, { elementType: "h2", children: "Let's try this again" }),
        /* @__PURE__ */ jsx47(Text21, { children: "Sorry, it doesn't look like we have anyone who can cover the limits you've selected. Not to worry. If you change your coverage limits above, you may be able to see available quotes." })
      ] })
    }
  );
}

// src/components/QuoteCards/NoQuotesReferral/NoQuotesReferral.tsx
import { Box as Box4, Link as Link7, Text as Text22 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext11 } from "@simplybusiness/services";
import { jsx as jsx48, jsxs as jsxs36 } from "react/jsx-runtime";
function NoQuotesReferral({
  quotes,
  referenceNumber,
  contactPhoneNumber,
  customerName,
  coverOptionsUrl
}) {
  const insurersPanel = (quotes || []).map((quote) => ({
    insurerName: quote.insurerNameParametrized,
    insurerLogo: quote.insurerLogo
  }));
  const { snowplow } = useSnowplowContext11();
  const handleCoverOptionsClick = () => snowplow?.trigger("coverOptionsLinkClicked");
  const handlePhoneNumberClick = () => snowplow?.trigger("phoneNumberLinkClicked");
  return /* @__PURE__ */ jsxs36(Box4, { className: "no-quotes-referral__container", children: [
    /* @__PURE__ */ jsxs36(Text22, { children: [
      "Hi ",
      /* @__PURE__ */ jsx48("strong", { children: customerName }),
      ", we've searched through our panel of trusted providers but we havent been able to find results to match your cover detail selections. You can expand your search by editing your choices on the",
      " ",
      /* @__PURE__ */ jsx48(Link7, { href: coverOptionsUrl, onClick: handleCoverOptionsClick, children: "Cover Options" }),
      " ",
      "page or through the filters above."
    ] }),
    /* @__PURE__ */ jsx48(Box4, { className: "no-quotes-referral__insurer-panel", children: insurersPanel.map((insurer, index) => /* @__PURE__ */ jsx48(
      Logo,
      {
        logoPath: insurer.insurerLogo,
        alt: insurer.insurerName,
        className: "no-quotes-referral-logo"
      },
      index
    )) }),
    /* @__PURE__ */ jsxs36(Text22, { className: "no-quotes-referral__summary", children: [
      "If you prefer to speak with us, our team will give you a call to see if we can find a quote that is right for your business. If you\u2019d prefer to call us, we're here to help on",
      " ",
      /* @__PURE__ */ jsx48(
        Link7,
        {
          href: `tel:${contactPhoneNumber}`,
          onClick: handlePhoneNumberClick,
          children: contactPhoneNumber
        }
      ),
      " ",
      "and give us your reference number ",
      /* @__PURE__ */ jsx48("strong", { children: referenceNumber })
    ] })
  ] });
}

// src/components/QuoteCards/PartialQuotesReferral/PartialQuotesReferral.tsx
import { Link as Link8, Text as Text23 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext12 } from "@simplybusiness/services";
import { jsx as jsx49, jsxs as jsxs37 } from "react/jsx-runtime";
function PartialQuotesReferral({
  quotes,
  customerName,
  coverOptionsUrl
}) {
  const insurersPanel = (quotes || []).map((quote) => ({
    insurerName: quote.insurerNameParametrized,
    insurerLogo: quote.insurerLogo
  }));
  const { snowplow } = useSnowplowContext12();
  const handleCoverOptionsClick = () => snowplow?.trigger("referralReasonsVariantBCoverOptionsLinkClicked");
  return /* @__PURE__ */ jsx49(
    "div",
    {
      "data-testid": "partial-quotes-referral",
      className: "partial-quotes-referral__container partial-quotes-referral__container-variant-b",
      children: /* @__PURE__ */ jsxs37("div", { className: "partial-quotes-referral__main", children: [
        /* @__PURE__ */ jsx49("div", { className: "partial-quotes-referral__info", children: /* @__PURE__ */ jsxs37(Text23, { children: [
          "Hi ",
          customerName,
          ", we've searched through our panel of trusted providers but we havent been able to find many results that match your cover detail selections. You can expand your search by editing your choices on the",
          " ",
          /* @__PURE__ */ jsx49(Link8, { href: coverOptionsUrl, onClick: handleCoverOptionsClick, children: "Cover Options" }),
          " ",
          "page or through the filters above."
        ] }) }),
        /* @__PURE__ */ jsx49("div", { className: "partial-quotes-referral__insurer-panel", children: insurersPanel.map((insurer, index) => /* @__PURE__ */ jsx49(
          Logo,
          {
            logoPath: insurer.insurerLogo,
            alt: insurer.insurerName,
            className: "partial-quotes-referral-logo"
          },
          index
        )) })
      ] })
    }
  );
}

// src/components/QuoteCards/sortByPrice.tsx
var convertCurrencyToNumber = (amount) => {
  const cleaned = amount?.replace(/[$£,]/g, "");
  return +(cleaned || Infinity);
};
var sortByPremium = (a, b) => {
  const left = convertCurrencyToNumber(a.premium ?? "0");
  const right = convertCurrencyToNumber(b.premium ?? "0");
  return left - right;
};
var sortByInstallmentAmount = (a, b) => {
  const left = convertCurrencyToNumber(a.installmentsAmount ?? "0");
  const right = convertCurrencyToNumber(b.installmentsAmount ?? "0");
  return left - right;
};

// src/components/QuoteCards/Unquotable/Unquotable.tsx
import { Link as Link9 } from "@simplybusiness/mobius";
import { jsx as jsx50, jsxs as jsxs38 } from "react/jsx-runtime";
var Unquotable = ({
  contactPhoneNumber,
  referenceNumber
}) => {
  return /* @__PURE__ */ jsxs38("div", { className: "mobius mobius-unquotable-card", children: [
    /* @__PURE__ */ jsx50("h2", { className: "mobius-unquotable-card__title", children: "No quotes showing?" }),
    /* @__PURE__ */ jsxs38("p", { className: "mobius-unquotable-card__text", children: [
      "Our team will give you a call to see if we can put this right and find you a quote. If you\u2019d prefer to call us, we\u2019re here to help on",
      " ",
      /* @__PURE__ */ jsx50(
        Link9,
        {
          className: "mobius-unquotable-card__contact-phone-number",
          href: `tel:${contactPhoneNumber}`,
          children: contactPhoneNumber
        }
      ),
      ". Please have your reference number",
      " ",
      /* @__PURE__ */ jsx50("strong", { className: "mobius-unquotable-card__reference-number", children: referenceNumber }),
      " ",
      "to hand."
    ] })
  ] });
};

// src/components/QuoteCards/QuoteCards.tsx
import { jsx as jsx51, jsxs as jsxs39 } from "react/jsx-runtime";
var QuoteCards = (props) => {
  const { quotes, ...otherProps } = props;
  const isToggling = useMutationStatus("toggleCovers");
  const { componentName, variant, customerName, coverOptionsUrl } = otherProps;
  const [paymentPeriod] = useAtom9(paymentToggleAtom);
  const { up } = useBreakpoint6(customBreakpoints);
  const isDesktop = up("lg");
  const [layout] = useAtom9(qcpLayoutToggleAtom);
  const unquotedQuotes = (quotes || []).filter(
    (quote) => quote.status !== "quoted"
  );
  const quotedQuotes = (quotes || []).filter(
    (quote) => quote.status === "quoted"
  );
  const eligibleForPartialQuotesReferral = variant === "referral-reasons";
  const withoutQdpDrawer = variant === "without-qdp-drawer";
  const variantWithoutQdpDrawer = withoutQdpDrawer || eligibleForPartialQuotesReferral;
  const sortedQuotes = quotedQuotes.sort(
    paymentPeriod === MONTHLY ? sortByInstallmentAmount : sortByPremium
  );
  const quotesList = [...sortedQuotes, ...unquotedQuotes];
  const quotedQuotesCount = quotedQuotes.length;
  const showNoQuotesReferral = withoutQdpDrawer && quotedQuotesCount === 0;
  if (showNoQuotesReferral) {
    return /* @__PURE__ */ jsx51(NoQuotesReferral, { quotes, ...otherProps });
  }
  const showPartialQuotesReferral = eligibleForPartialQuotesReferral && customerName !== void 0 && coverOptionsUrl !== void 0;
  const showUnquotable = variant === "unsupported-trade-referral";
  const hideNoQuoteCard = showPartialQuotesReferral;
  const isGridLayout = layout === "grid";
  const classes = classNames17("mobius-quote-cards__grid", {
    "--is-horizontal": isGridLayout && !isDesktop || !isGridLayout && variantWithoutQdpDrawer,
    "--is-grid": isGridLayout && isDesktop
  });
  if (isToggling) {
    return /* @__PURE__ */ jsx51(QuotesSkeleton, { ...props });
  }
  if (showUnquotable) {
    return /* @__PURE__ */ jsx51(
      Unquotable,
      {
        contactPhoneNumber: otherProps.contactPhoneNumber,
        referenceNumber: otherProps.referenceNumber
      }
    );
  }
  return /* @__PURE__ */ jsxs39(
    Section,
    {
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      children: [
        /* @__PURE__ */ jsx51("div", { role: "list", className: classes, "data-testid": "quote-cards-list", children: quotes && quotes.length > 0 ? quotesList?.map((quote) => /* @__PURE__ */ jsx51(
          QuoteCard,
          {
            quote,
            variant,
            hideNoQuoteCard
          },
          quote.id
        )) : /* @__PURE__ */ jsx51(NoQuotesCard, {}) }),
        showPartialQuotesReferral && /* @__PURE__ */ jsx51(
          PartialQuotesReferral,
          {
            quotes: unquotedQuotes,
            customerName,
            coverOptionsUrl
          }
        )
      ]
    }
  );
};

// src/components/QuoteCards/ExperimentGridView/QuoteCards.tsx
import { useAtom as useAtom12 } from "jotai";
import classNames20 from "classnames/dedupe";
import { useBreakpoint as useBreakpoint8 } from "@simplybusiness/mobius";

// src/components/QuoteCards/ExperimentGridView/PartialQuotesReferral/PartialQuotesReferral.tsx
import { Link as Link10, Text as Text24 } from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext13 } from "@simplybusiness/services";
import { jsx as jsx52, jsxs as jsxs40 } from "react/jsx-runtime";
function PartialQuotesReferral2({
  quotes,
  customerName,
  coverOptionsUrl,
  view
}) {
  const insurersPanel = (quotes || []).map((quote) => ({
    insurerName: quote.insurerNameParametrized,
    insurerLogo: quote.insurerLogo
  }));
  const { snowplow } = useSnowplowContext13();
  const handleCoverOptionsClick = () => snowplow?.trigger("referralReasonsVariantBCoverOptionsLinkClicked");
  return /* @__PURE__ */ jsx52(
    "div",
    {
      "data-testid": `partial-quotes-referral-${view}`,
      className: `partial-quotes-referral__container partial-quotes-referral__container--${view} partial-quotes-referral`,
      children: /* @__PURE__ */ jsxs40("div", { className: "partial-quotes-referral__main", children: [
        /* @__PURE__ */ jsx52("div", { className: "partial-quotes-referral__info", children: /* @__PURE__ */ jsxs40(Text24, { children: [
          "Hi ",
          customerName,
          ", we've searched through our panel of trusted providers but we havent been able to find many results that match your cover detail selections. You can expand your search by editing your choices on the",
          " ",
          /* @__PURE__ */ jsx52(Link10, { href: coverOptionsUrl, onClick: handleCoverOptionsClick, children: "Cover Options" }),
          " ",
          "page or through the filters above."
        ] }) }),
        /* @__PURE__ */ jsx52("div", { className: "partial-quotes-referral__insurer-panel", children: insurersPanel.map((insurer, index) => /* @__PURE__ */ jsx52(
          Logo,
          {
            logoPath: insurer.insurerLogo,
            alt: insurer.insurerName,
            className: "partial-quotes-referral-logo"
          },
          index
        )) })
      ] })
    }
  );
}

// src/components/QuoteCards/ExperimentGridView/QuoteCard/QuoteCard.tsx
import classNames19 from "classnames/dedupe";

// src/components/QuoteCards/ExperimentGridView/QuoteCard/Coverage/CoverageUK/Coverage.tsx
import {
  Accordion as Accordion4,
  Box as Box5,
  Modal as Modal2,
  Text as Text25,
  useBreakpoint as useBreakpoint7
} from "@simplybusiness/mobius";
import { useSnowplowContext as useSnowplowContext14 } from "@simplybusiness/services";
import classNames18 from "classnames/dedupe";
import { useCallback as useCallback2, useState as useState4 } from "react";
import { useAtom as useAtom10 } from "jotai/index";
import { Fragment as Fragment12, jsx as jsx53, jsxs as jsxs41 } from "react/jsx-runtime";
var Coverage2 = ({ covers, className }) => {
  const [open, setOpen] = useState4(false);
  const [modalData, setModalData] = useState4({
    displayName: "",
    excesses: []
  });
  const { down } = useBreakpoint7(customBreakpoints);
  const isMobileDevice = down("md");
  const { snowplow } = useSnowplowContext14();
  const classes = classNames18("mobius-quote-card__coverage", className);
  const handleOpen = useCallback2(
    ({ displayName, excesses }) => {
      setModalData({ displayName, excesses });
      setOpen(true);
    },
    []
  );
  const handleAccordionChange = useCallback2(
    (open2) => {
      snowplow?.trigger("coverageInfoClicked", {
        deviceType: isMobileDevice ? "mobile" : "desktop",
        open: open2
      });
    },
    [isMobileDevice, snowplow]
  );
  const [layout] = useAtom10(qcpLayoutToggleAtom);
  const isGridLayout = layout === "grid";
  return /* @__PURE__ */ jsx53(Fragment12, { children: /* @__PURE__ */ jsxs41(Box5, { className: classes, "data-testid": "quote-coverage-uk", children: [
    isMobileDevice || isGridLayout ? /* @__PURE__ */ jsx53(
      Accordion4,
      {
        showText: "Show coverage info",
        hideText: "Hide coverage info",
        onChange: handleAccordionChange,
        startOpen: true,
        children: /* @__PURE__ */ jsx53(CoverageItems, { covers, onOpen: handleOpen })
      }
    ) : /* @__PURE__ */ jsxs41(Fragment12, { children: [
      /* @__PURE__ */ jsx53(Text25, { className: "mobius-quote-card__coverage-title", variant: "h3", children: "Coverage Information" }),
      /* @__PURE__ */ jsx53(CoverageItems, { covers, onOpen: handleOpen })
    ] }),
    /* @__PURE__ */ jsxs41(
      Modal2,
      {
        className: "mobius-quote-card__coverage-item-excess-modal",
        isOpen: open,
        onClose: () => setOpen(false),
        children: [
          /* @__PURE__ */ jsxs41(Modal2.Header, { children: [
            modalData.displayName,
            " excess"
          ] }),
          /* @__PURE__ */ jsx53(Modal2.Content, { children: modalData.excesses.map((cover) => /* @__PURE__ */ jsx53(
            CoverLimit,
            {
              ...cover,
              product: modalData.displayName
            },
            cover.title
          )) })
        ]
      }
    )
  ] }) });
};

// src/components/QuoteCards/ExperimentGridView/QuoteCard/Coverage/index.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var Coverage3 = (props) => {
  return /* @__PURE__ */ jsx54(Coverage2, { ...props });
};

// src/components/QuoteCards/ExperimentGridView/QuoteCard/PricingUK/Pricing.tsx
import { calendarClock as calendarClock4 } from "@simplybusiness/icons";
import { Flex as Flex15, Icon as Icon10, Text as Text26 } from "@simplybusiness/mobius";
import { useAtom as useAtom11 } from "jotai";
import { Fragment as Fragment13, jsx as jsx55, jsxs as jsxs42 } from "react/jsx-runtime";
var Pricing4 = ({
  quote,
  showInstallmentsInformation
}) => {
  const {
    installmentsAvailable,
    downpaymentAmount,
    installmentsAmount,
    installmentsCount,
    installmentsTotal,
    premium
  } = quote;
  const [paymentPeriod] = useAtom11(paymentToggleAtom);
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const showInstalments = isMonthly && hasInstallments && showInstallmentsInformation;
  const instalmentsText = downpaymentAmount ? `Then ${installmentsCount} monthly payments of` : `${installmentsCount} monthly payments of`;
  return /* @__PURE__ */ jsxs42("div", { className: "mobius-quote-card__pricing --is-uk", children: [
    /* @__PURE__ */ jsxs42(
      Flex15,
      {
        alignItems: "flex-start",
        gap: "xxs",
        justifyContent: "space-between",
        className: "mobius-quote-card__pricing-flex",
        children: [
          /* @__PURE__ */ jsx55(
            Premium2,
            {
              isMonthly,
              hasInstallments,
              installmentsAmount,
              premium
            }
          ),
          /* @__PURE__ */ jsx55(QuoteCardBadges, { className: "--is-uk --is-desktop", children: /* @__PURE__ */ jsx55(
            QuoteCardBadge,
            {
              icon: /* @__PURE__ */ jsx55(
                Icon10,
                {
                  icon: calendarClock4,
                  color: "var(--color-accent)",
                  title: "Policy duration information"
                }
              ),
              "data-testid": "quote-card-badge-12-month-policy",
              children: "12 month policy"
            }
          ) })
        ]
      }
    ),
    showInstalments && /* @__PURE__ */ jsxs42("div", { className: "mobius-quote-card__instalments-grid", children: [
      downpaymentAmount && /* @__PURE__ */ jsxs42(Fragment13, { children: [
        /* @__PURE__ */ jsx55(Text26, { children: "First payment" }),
        /* @__PURE__ */ jsx55(Text26, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx55("strong", { children: downpaymentAmount }) })
      ] }),
      /* @__PURE__ */ jsx55(Text26, { children: instalmentsText }),
      /* @__PURE__ */ jsx55(Text26, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx55("strong", { children: installmentsAmount }) }),
      /* @__PURE__ */ jsx55(Text26, { children: "Total over 12 months" }),
      /* @__PURE__ */ jsx55(Text26, { className: "mobius-quote-card__instalment_amount", children: /* @__PURE__ */ jsx55("strong", { children: installmentsTotal }) })
    ] }),
    /* @__PURE__ */ jsx55(QuoteCardBadges, { className: "--is-mobile", children: /* @__PURE__ */ jsx55(
      QuoteCardBadge,
      {
        icon: /* @__PURE__ */ jsx55(
          Icon10,
          {
            icon: calendarClock4,
            color: "var(--color-accent)",
            title: "Policy duration information"
          }
        ),
        "data-testid": "quote-card-badge-12-month-policy",
        children: "12 month policy"
      }
    ) })
  ] });
};

// src/components/QuoteCards/ExperimentGridView/QuoteCard/QuoteCard.tsx
import { Fragment as Fragment14, jsx as jsx56, jsxs as jsxs43 } from "react/jsx-runtime";
var QuoteCard2 = ({
  quote,
  variant,
  hideNoQuoteCard
}) => {
  const { status } = quote;
  const insurer = quote.covers[0]?.coverInsurer;
  const insurerParametrized = insurer?.toLowerCase().replaceAll(" ", "-");
  const chopinTestId = `buy-${insurerParametrized}-block`;
  const { isUK } = useLocale();
  const filteredCovers = filterCovers(quote);
  const classes = classNames19("mobius-quote-card");
  if (status === "referred") {
    if (hideNoQuoteCard === true) {
      return /* @__PURE__ */ jsx56(Fragment14, {});
    }
    return /* @__PURE__ */ jsx56(NoQuoteCard, { insurer, insurerLogo: quote.insurerLogo });
  }
  return /* @__PURE__ */ jsxs43(
    "div",
    {
      role: "listitem",
      className: classes,
      id: chopinTestId,
      "data-testid": "quote-cards-list-item",
      children: [
        isUK ? /* @__PURE__ */ jsx56(
          HeaderUK,
          {
            logo: quote.insurerLogo,
            underWriterText: quote.underwriter
          }
        ) : /* @__PURE__ */ jsx56(
          Header,
          {
            logo: quote.insurerLogo,
            insurerFinancialStrengthRatings: quote.ratings
          }
        ),
        isUK ? /* @__PURE__ */ jsx56(Pricing4, { quote, displayBadges: true, showInstallmentsInformation: true }) : /* @__PURE__ */ jsx56(Pricing2, { quote, displayBadges: true, showInstallmentsInformation: true }),
        /* @__PURE__ */ jsx56(
          Coverage3,
          {
            covers: filteredCovers,
            additionalCoverage: quote.additionalCoverage,
            displayVariant: "qcp",
            withModal: true,
            variant,
            upsells: quote.upsells
          }
        ),
        /* @__PURE__ */ jsx56(QuoteActions2, { quote, variant })
      ]
    }
  );
};

// src/components/QuoteCards/ExperimentGridView/QuoteCards.tsx
import { useState as useState5, useEffect as useEffect2 } from "react";
import { Fragment as Fragment15, jsx as jsx57, jsxs as jsxs44 } from "react/jsx-runtime";
var useHydrated = () => {
  const [hydrated, setHydrated] = useState5(false);
  useEffect2(() => {
    setHydrated(true);
  }, []);
  return hydrated;
};
var QuoteCards2 = (props) => {
  const { quotes, ...otherProps } = props;
  const isToggling = useMutationStatus("toggleCovers");
  const { componentName, variant, customerName, coverOptionsUrl } = otherProps;
  const [paymentPeriod] = useAtom12(paymentToggleAtom);
  const unquotedQuotes = [];
  const quotedQuotes = [];
  (quotes || []).forEach((quote) => {
    if (quote.status === "quoted") {
      quotedQuotes.push(quote);
    } else {
      unquotedQuotes.push(quote);
    }
  });
  const eligibleForReferralReasons = variant === "referral-reasons";
  const withoutQdpDrawer = variant === "without-qdp-drawer";
  const variantWithoutQdpDrawer = withoutQdpDrawer || eligibleForReferralReasons;
  const quotedQuotesCount = quotedQuotes.length;
  const showReferral = withoutQdpDrawer && quotedQuotesCount === 0;
  const showPartialQuotesReferral = eligibleForReferralReasons && customerName !== void 0 && coverOptionsUrl !== void 0;
  const showUnquotable = variant === "unsupported-trade-referral";
  const hideNoQuoteCard = showPartialQuotesReferral;
  const { up } = useBreakpoint8(customBreakpoints);
  const isDesktop = up("lg");
  const hydrated = useHydrated();
  const [layout] = useAtom12(qcpLayoutToggleAtom);
  if (!hydrated) {
    return /* @__PURE__ */ jsx57(QuotesSkeleton, { ...props });
  }
  const isGridLayout = layout === "grid";
  const classes = classNames20(
    "mobius-quote-cards__grid",
    "--is-grid-view-treatment",
    {
      "--is-horizontal": isGridLayout && !isDesktop || !isGridLayout && variantWithoutQdpDrawer,
      "--is-grid": isGridLayout && isDesktop
    }
  );
  if (isToggling) {
    return /* @__PURE__ */ jsx57(QuotesSkeleton, { ...props });
  }
  if (showUnquotable) {
    return /* @__PURE__ */ jsx57(
      Unquotable,
      {
        contactPhoneNumber: otherProps.contactPhoneNumber,
        referenceNumber: otherProps.referenceNumber
      }
    );
  }
  if (showReferral) {
    return /* @__PURE__ */ jsx57(NoQuotesReferral, { quotes, ...otherProps });
  }
  const isQuotesCountMultipleOfThree = !!quotes && quotes.length % 3 === 0;
  let partialQuotesSignpostDisplay = null;
  if (showPartialQuotesReferral) {
    partialQuotesSignpostDisplay = isQuotesCountMultipleOfThree ? "banner" : "grid";
  }
  const referredQuotes = [];
  const nonReferredQuotes = [];
  (quotes || []).forEach((q) => {
    if (q.status === "referred") {
      referredQuotes.push(q);
    } else {
      nonReferredQuotes.push(q);
    }
  });
  const sortedQuotes = nonReferredQuotes.sort(
    paymentPeriod === MONTHLY ? sortByInstallmentAmount : sortByPremium
  );
  const showNoQuotesCard = sortedQuotes.length === 0;
  const renderQuoteCards = (quotesToRender) => quotesToRender.map((quote) => /* @__PURE__ */ jsx57(
    QuoteCard2,
    {
      quote,
      variant,
      hideNoQuoteCard
    },
    quote.id
  ));
  return /* @__PURE__ */ jsxs44(
    Section,
    {
      ...componentName ? { "data-component": componentName } : {},
      ...variant ? { "data-variant": variant } : {},
      children: [
        /* @__PURE__ */ jsx57("div", { role: "list", className: classes, "data-testid": "quote-cards-list", children: showNoQuotesCard ? /* @__PURE__ */ jsx57(NoQuotesCard, {}) : /* @__PURE__ */ jsxs44(Fragment15, { children: [
          renderQuoteCards(sortedQuotes),
          !isGridLayout && referredQuotes.length > 0 && renderQuoteCards(referredQuotes),
          partialQuotesSignpostDisplay === "grid" && /* @__PURE__ */ jsx57(
            PartialQuotesReferral2,
            {
              quotes: unquotedQuotes,
              customerName: customerName || "",
              coverOptionsUrl: coverOptionsUrl || "",
              view: isGridLayout ? "grid" : "banner"
            }
          )
        ] }) }),
        isGridLayout && !showPartialQuotesReferral && referredQuotes.length > 0 && /* @__PURE__ */ jsx57("div", { className: classes, children: renderQuoteCards(referredQuotes) }),
        partialQuotesSignpostDisplay === "banner" && /* @__PURE__ */ jsx57(
          PartialQuotesReferral2,
          {
            quotes: unquotedQuotes,
            customerName: customerName || "",
            coverOptionsUrl: coverOptionsUrl || "",
            view: "banner"
          }
        )
      ]
    }
  );
};

// src/components/QuoteCards/index.tsx
import { jsx as jsx58 } from "react/jsx-runtime";
var QuoteCards3 = (props) => {
  const isGridView = ["treatment_grid", "treatment_list"].includes(
    useExperiment("uk2505Gridvslistview") || ""
  );
  if (isGridView) {
    return /* @__PURE__ */ jsx58(QuoteCards2, { ...props });
  }
  return /* @__PURE__ */ jsx58(QuoteCards, { ...props });
};

export {
  AgentActions,
  AgentInformation,
  useSetQcpState,
  useQuoteComparisonPageState,
  CoverToggle,
  CoverTogglesContainer3 as CoverTogglesContainer,
  Headline2 as Headline,
  QuoteCard,
  QuoteCards3 as QuoteCards
};
